import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import { sb, API_URL } from '../utils/config'
import { loadAllVideoUrls, getVideoMp4, getVideoThumb } from '../utils/airtableVideo'
import { StudioAdminToggle, StudioAdminLogsButton, StudioAdminTopFormatsButton, StudioAdminAdsButton, StudioAdminSeoButton, StudioAdminHubLink, StudioAdminResetButton, StudioAdminGuestToggle, StudioAdminVerticalToggle, StudioAdminSimulateSportButton, AdminSourceLink, Chevron, StudioUpgradeModal, StudioAnalysisPaywallModal, goToStudioCheckout, AccountScore, FormatDetailModal, PasswordRecoveryModal } from './StudioPage'
import { TrameContent } from '../utils/beatGuide'
import { cleanScriptTemplate } from '../utils/scriptText'
import { studioEvents } from '../utils/metaPixel'
import StudioAnalysisTab, { TourVideoAnalysisDemo, VaGiftFiche } from './StudioAnalysisTab'
import { getSlot, getVariantKey, initUiExperiments, subscribeUiExperiments } from '../utils/uiExperiments'
import StudioOnboarding, { ActivationChecklist, markActivationStep } from '../components/StudioOnboarding'
import AvisBanner from '../components/AvisBanner'
import merlinImg from '../assets/merlin.png'
import merlinFound from '../assets/Merlin_succes-removebg-preview.png'

import VERTICALES_CFG from '../config/verticales.json'
// Verticale active (chip topbar) : lue une fois au chargement du module —
// suffisant, tout changement de verticale passe par un reload complet.
const ACTIVE_VERTICAL = (() => {
  try { return VERTICALES_CFG[sessionStorage.getItem('studio_vertical') || localStorage.getItem('studio_vertical_persona')] || null } catch { return null }
})()

/**
 * StudioPro — interface complète après paiement.
 *
 * 3 vues internes :
 *   - home : Drop hebdo (5 formats curated) + raccourci coffre + lien library
 *   - library : Scroll infini avec filtres (type, mécanique, durée)
 *   - vault : Coffre personnel (saved/draft/published)
 *
 * Routing interne via state local `view`. Pas de react-router pour rester
 * dans le wrapper StudioPage actuel.
 */

const FILTER_VUES = [
  { key: '0-100k',  label: '0 - 100k vues' },
  { key: '100k-1m', label: '100k - 1M' },
  { key: '1m-10m',  label: '1M - 10M' },
  { key: '10m+',    label: '10M+' },
]

const FILTER_TYPES = [
  { key: 'liste', label: 'Liste' },
  { key: 'tutoriel', label: 'Tutoriel' },
  { key: 'comparatif', label: 'Comparatif' },
  { key: 'temoignage', label: 'Témoignage' },
  { key: 'hot_take', label: 'Hot take' },
  { key: 'micro_trottoir', label: 'Micro-trottoir' },
  { key: 'demo', label: 'Démonstration' },
  { key: 'question', label: 'Quiz / Question' },
  { key: 'monologue', label: 'Face caméra' },
]
const FILTER_MECHANICS = [
  { key: 'curiosite', label: 'Curiosité' },
  { key: 'identification', label: 'Identification' },
  { key: 'provocation', label: 'Provocation' },
  { key: 'valeur', label: 'Valeur utile' },
  { key: 'humour', label: 'Humour' },
  { key: 'emotion', label: 'Émotion' },
]
const FILTER_DURATIONS = [
  { key: 'short', label: '< 30s' },
  { key: 'medium', label: '30-60s' },
  { key: 'long', label: '> 60s' },
]

// ═══════════════════════════════════════════════════════════════
// STUDIO PROFILE MODAL — compte + abonnement + Stripe Customer Portal
// ═══════════════════════════════════════════════════════════════
export function StudioProfileModal({ user, userProfile, studioPlan, onClose, onSignOut, autoRetention = false, __demoRetention = null }) {
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState(null)
  // Écran de rétention (décision Antonin 23/07) : si l'user est EN ESSAI et
  // part vers le portail (= très probablement pour annuler), on lui montre
  // d'abord ce qu'il perd + l'offre de prolonger l'essai de 7 jours.
  const [retention, setRetention] = useState(__demoRetention) // { trial_end, extended, is_pro_plus } (__demoRetention = harnais screenshot)
  const [extending, setExtending] = useState(false)
  const [extendedUntil, setExtendedUntil] = useState(null)
  const [switching, setSwitching] = useState(false)
  const [switchedToCreator, setSwitchedToCreator] = useState(false)
  const [, forceExp] = useState(0)
  // A/B downsell rétention : charge les expériences pour que getVariantKey soit
  // fiable dans la modale (elle peut s'ouvrir hors du StudioPro parent).
  useEffect(() => { initUiExperiments(); return subscribeUiExperiments(() => forceExp((x) => x + 1)) }, [])

  const isPaid = !!(studioPlan && ['pro', 'pro_plus', 'creator', 'agency'].includes(studioPlan))
  const isAdmin = (user?.email || '').toLowerCase() === 'antonin.girardeau.pro@gmail.com'
  const memberSince = userProfile?.created_at
    ? new Date(userProfile.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    : null

  function gotoPortal() {
    // Portal login link statique configuré dans Stripe Dashboard. Stripe
    // accepte ?prefilled_email pour skip l'étape de saisie email → tu cliques
    // sur 'Envoyer le lien' direct. Magic link arrive par mail 30s plus tard.
    const PORTAL_LOGIN = 'https://billing.stripe.com/p/login/6oU6oI3cw9n4aIy4Vpa3u00'
    const email = user?.email || ''
    window.location.href = email
      ? `${PORTAL_LOGIN}?prefilled_email=${encodeURIComponent(email)}`
      : PORTAL_LOGIN
  }

  // Flow de RÉSILIATION IN-APP (décision Antonin 23/07) : deux boutons
  // distincts. « Factures » → portail Stripe direct (gotoPortal). « Résilier »
  // → ce flow : essai non prolongé → écran de rétention (+7j) ; sinon →
  // confirmation simple. La résiliation elle-même se fait via notre API
  // (cancel_at_period_end), plus besoin du portail.
  const [cancelConfirm, setCancelConfirm] = useState(null) // { access_until? } pour les non-essais
  const [canceledUntil, setCanceledUntil] = useState(null)
  const [canceling, setCanceling] = useState(false)

  async function startCancelFlow(fromDeepLink = false) {
    setPortalLoading(true); setPortalError(null)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      if (token) {
        const r = await fetch(`${API_URL}/api/stripe/sub-info`, { headers: { Authorization: `Bearer ${token}` } })
        const info = await r.json().catch(() => ({}))
        // Drapeau deal serveur → réhydrate le localStorage de CE navigateur
        // (le drapeau ne survit pas aux sauts webview → Safari sinon).
        try { if (info?.deal_offer) localStorage.setItem('studio_deal_offer', '1') } catch { /* best effort */ }
        if (info?.trialing && !info.extended && !info.cancel_scheduled) {
          try { studioEvents.tabClicked?.({ tab: 'retention_screen_shown', source_page: fromDeepLink ? 'email_deeplink' : 'profile_modal' }) } catch {}
          setRetention(info)
          setPortalLoading(false)
          return
        }
        // PAYANT (Antonin 03/08) : on porte is_pro_plus / downsell_used /
        // period_end dans l'écran de confirmation pour y afficher le filet 39€
        // (le churn payant se concentre à J25-J28, sans aucun filet jusqu'ici).
        if (info?.active && !fromDeepLink) {
          setCancelConfirm(info)
          setPortalLoading(false)
          return
        }
      }
    } catch { /* sub-info indisponible : confirmation simple */ }
    if (fromDeepLink) { setPortalLoading(false); return }
    setCancelConfirm({})
    setPortalLoading(false)
  }

  async function cancelNow(source) {
    setCanceling(true); setPortalError(null)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      const r = await fetch(`${API_URL}/api/stripe/cancel-subscription`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      const out = await r.json()
      if (out.canceled || out.already_canceled) {
        try { studioEvents.tabClicked?.({ tab: 'canceled_inapp', source_page: source }) } catch {}
        setCanceledUntil(out.access_until)
        setRetention(null); setCancelConfirm(null)
      } else {
        setPortalError(out.error || 'Impossible de résilier, réessaie ou écris-moi en répondant à un mail.')
      }
    } catch (e) { setPortalError(e.message) }
    setCanceling(false)
  }

  // Deep link ?extend=1 depuis le mail J-3 : ouvre direct l'écran de rétention
  useEffect(() => {
    if (autoRetention) startCancelFlow(true)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function extendTrial() {
    setExtending(true)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      const r = await fetch(`${API_URL}/api/stripe/extend-trial`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      const out = await r.json()
      if (out.extended || out.already_extended) {
        try { studioEvents.tabClicked?.({ tab: 'trial_extended', source_page: 'retention_screen' }) } catch {}
        setExtendedUntil(out.trial_end)
      } else {
        setPortalError(out.error || 'Impossible de prolonger, réessaie ou réponds au mail.')
      }
    } catch (e) {
      setPortalError(e.message)
    }
    setExtending(false)
  }

  // Downsell 89€→39€ SANS re-paiement : bascule le prix de l'abo existant en
  // gardant l'essai (voir /api/stripe/switch-to-creator). Filet anti-churn au
  // clic « résilier » pour les Pro+ (A/B retention_downsell).
  async function switchToCreator() {
    setSwitching(true); setPortalError(null)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      const r = await fetch(`${API_URL}/api/stripe/switch-to-creator`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      const out = await r.json()
      if (out.switched || out.already_creator) {
        try { studioEvents.tabClicked?.({ tab: 'downsell_creator_taken', source_page: 'retention_screen' }) } catch {}
        setSwitchedToCreator(true)
      } else {
        setPortalError(out.error || 'Impossible de basculer, réessaie ou réponds au mail.')
      }
    } catch (e) { setPortalError(e.message) }
    setSwitching(false)
  }

  // A/B downsell rétention (retention_downsell, bras A = filet 39€ visible ;
  // B = contrôle). Pro+ en essai uniquement, une seule fois (downsell_used).
  // Override URL ?rdownsell=A|B pour tester chaque bras à la main.
  const downsellVariant = (() => {
    try { const o = new URLSearchParams(window.location.search).get('rdownsell'); if (o === 'A' || o === 'B') return o } catch { /* noop */ }
    return getVariantKey('retention_downsell')
  })()
  const showDownsell = downsellVariant === 'A' && !!retention?.is_pro_plus && !retention?.downsell_used
  useEffect(() => {
    if (retention && !extendedUntil && !switchedToCreator && showDownsell) {
      try { studioEvents.tabClicked?.({ tab: 'retention_downsell_shown', source_page: 'retention_screen' }) } catch { /* noop */ }
    }
  }, [retention, showDownsell, extendedUntil, switchedToCreator])
  // Impression du filet 39€ côté PAYANTS (extension 03/08) : même event, source
  // distincte pour mesurer montré → accepté sur chaque population.
  useEffect(() => {
    if (cancelConfirm && cancelConfirm.is_pro_plus && !cancelConfirm.downsell_used && downsellVariant === 'A' && !switchedToCreator) {
      try { studioEvents.tabClicked?.({ tab: 'retention_downsell_shown', source_page: 'cancel_confirm_paid' }) } catch { /* noop */ }
    }
  }, [cancelConfirm, downsellVariant, switchedToCreator])

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      style={{ background: 'rgba(15,10,26,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* ═══ RÉSILIATION FAITE (in-app) ═══ */}
        {canceledUntil && (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">👋</div>
            <p className="text-lg font-bold text-ink mb-2">C'est fait, abonnement résilié</p>
            <p className="text-sm text-ink-muted mb-5">
              Aucun prélèvement ne sera fait. Ton accès reste ouvert jusqu'au{' '}
              <strong className="text-ink">{new Date(canceledUntil).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>,
              profites-en. Tu peux te réabonner quand tu veux.
            </p>
            <button onClick={onClose}
              className="w-full py-3 rounded-xl font-bold text-sm text-white"
              style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' }}>
              Fermer
            </button>
          </div>
        )}
        {/* ═══ CONFIRMATION SIMPLE (abonné payant, pas d'essai) ═══ */}
        {!canceledUntil && cancelConfirm && (
          switchedToCreator ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-lg font-bold text-ink mb-2">C'est fait, tu passes en Créateur</p>
              <p className="text-sm text-ink-muted mb-5">
                Tu gardes tout Pro+ jusqu'au{' '}
                <strong className="text-ink">{cancelConfirm.period_end ? new Date(cancelConfirm.period_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : 'bout de ta période payée'}</strong>
                {' '}(déjà payé), puis ce sera <strong className="text-ink">39€/mois</strong> au lieu de 89€. Rien à faire, rien n'est prélevé aujourd'hui.
              </p>
              <button onClick={onClose}
                className="w-full py-3 rounded-xl font-bold text-sm text-white"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                Retourner à mes formats
              </button>
            </div>
          ) : (
          <div>
            <p className="text-lg font-bold text-ink mb-2">Résilier ton abonnement ?</p>
            <p className="text-sm text-ink-muted mb-4">
              Ton accès restera ouvert jusqu'à la fin de la période déjà payée, puis il s'arrêtera. Aucun prélèvement ensuite.
            </p>
            <button onClick={() => setCancelConfirm(null)}
              className="w-full py-3 rounded-xl font-bold text-sm text-white mb-2"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
              Garder mon abonnement
            </button>
            {/* Filet 39€ pour les PAYANTS Pro+ (extension 03/08, churn J25-J28) :
                bascule à la FIN de la période payée (il garde son Pro+ payé),
                0 prélèvement immédiat. Même A/B retention_downsell (bras A). */}
            {downsellVariant === 'A' && cancelConfirm.is_pro_plus && !cancelConfirm.downsell_used && (
              <button onClick={switchToCreator} disabled={switching || canceling}
                className="w-full py-3 rounded-xl font-bold text-[13.5px] mb-2 disabled:opacity-60"
                style={{ background: '#fff', color: 'var(--merlin-violet, #7c3aed)', border: '2px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.55)' }}>
                {switching ? 'Un instant…' : "89€ c'est trop ? Continue en Créateur à 39€/mois"}
              </button>
            )}
            <button onClick={() => cancelNow('cancel_confirm')} disabled={canceling}
              className="w-full py-2.5 text-[13px] text-ink-muted hover:text-ink underline underline-offset-2 disabled:opacity-60">
              {canceling ? 'Résiliation…' : 'Confirmer la résiliation'}
            </button>
            {portalError && <p className="text-xs text-rose-600 mt-2">{portalError}</p>}
          </div>
          )
        )}
        {/* ═══ ÉCRAN DE RÉTENTION (essai en cours → clic « résilier ») ═══ */}
        {!canceledUntil && !cancelConfirm && retention && (
          <div>
            {extendedUntil ? (
              <div className="text-center py-6">
                <div className="text-4xl mb-3">✅</div>
                <p className="text-lg font-bold text-ink mb-2">C'est fait, ton essai continue</p>
                <p className="text-sm text-ink-muted mb-5">
                  7 jours de plus, gratuits. Ton essai se termine maintenant le{' '}
                  <strong className="text-ink">{new Date(extendedUntil).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>.
                  Aucun prélèvement avant cette date.
                </p>
                <button onClick={onClose}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                  Retourner à mes formats
                </button>
              </div>
            ) : switchedToCreator ? (
              <div className="text-center py-6">
                <div className="text-4xl mb-3">✅</div>
                <p className="text-lg font-bold text-ink mb-2">C'est fait, tu passes en Créateur</p>
                <p className="text-sm text-ink-muted mb-5">
                  Ton essai continue normalement, rien n'a été prélevé. À la fin de l'essai, ce sera{' '}
                  <strong className="text-ink">39€/mois</strong> au lieu de 89€. Tu gardes les meilleurs formats adaptés à ton compte et la bibliothèque entière.
                </p>
                <button onClick={onClose}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                  Retourner à mes formats
                </button>
              </div>
            ) : (
              <div>
                <p className="text-lg font-bold text-ink mb-1">Avant de continuer…</p>
                <p className="text-sm text-ink-muted mb-4">
                  Ton essai se termine le{' '}
                  <strong className="text-ink">{retention.trial_end ? new Date(retention.trial_end).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'bientôt'}</strong>.
                  Si tu annules, voilà ce qui s'arrête :
                </p>
                <div className="space-y-2 mb-5">
                  {[
                    ['📝', 'Les meilleurs formats adaptés à ton compte'],
                    ['🔍', "L'analyse en profondeur de tes vidéos"],
                    ['📊', "L'analyse complète de ton compte et son suivi"],
                    ['📚', 'La bibliothèque entière de formats viraux'],
                  ].map(([emo, txt], i) => (
                    <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(15,16,64,0.03)' }}>
                      <span className="text-base leading-none mt-0.5">{emo}</span>
                      <span className="text-[13px] text-ink/80 leading-snug">{txt}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[13px] text-ink-muted mb-3">
                  Pas encore eu le temps de tout tester ? Prends 7 jours de plus, gratuits. Sans engagement, tu peux toujours annuler après.
                </p>
                <button onClick={extendTrial} disabled={extending}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white mb-2 disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
                  {extending ? 'Un instant…' : 'Prolonger mon essai gratuit de 7 jours'}
                </button>
                {/* Downsell 89€→39€ (A/B retention_downsell bras A, Pro+ seulement) :
                    bascule sans re-paiement, essai gardé intact. Bouton violet
                    secondaire = pas le CTA par défaut, self-selection budget. */}
                {showDownsell && (
                  <button onClick={switchToCreator} disabled={switching || canceling}
                    className="w-full py-3 rounded-xl font-bold text-[13.5px] mb-2 disabled:opacity-60"
                    style={{ background: '#fff', color: 'var(--merlin-violet, #7c3aed)', border: '2px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.55)' }}>
                    {switching ? 'Un instant…' : "89€ c'est trop ? Continue en Créateur à 39€"}
                  </button>
                )}
                <button onClick={() => cancelNow('retention_screen')} disabled={canceling}
                  className="w-full py-3 rounded-xl font-bold text-sm disabled:opacity-60"
                  style={{ background: '#fff', color: 'var(--merlin-accent, #fe8204)', border: '2px solid var(--merlin-accent, #fe8204)' }}>
                  {canceling ? 'Résiliation…' : 'Résilier quand même'}
                </button>
                {portalError && <p className="text-xs text-rose-600 mt-2">{portalError}</p>}
              </div>
            )}
          </div>
        )}
        {!retention && !cancelConfirm && !canceledUntil && (<>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))', color: 'white' }}>
              <span className="text-base font-bold">{(user?.email?.[0] || '?').toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-ink leading-tight truncate">
                {userProfile?.display_name || user?.email?.split('@')[0] || 'Compte'}
              </p>
              <p className="text-[11px] text-ink-muted truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-ink-muted hover:text-ink leading-none p-1 shrink-0">×</button>
        </div>

        {/* Plan card */}
        <div className="rounded-xl p-4 mb-4 border"
          style={{
            background: isPaid
              ? 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.08), rgba(245,158,11,0.04))'
              : 'rgba(0,0,0,0.02)',
            borderColor: isPaid ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.20)' : 'rgba(0,0,0,0.06)',
          }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Abonnement Studio</p>
            {isPaid && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-orange/15 text-orange">
                Actif
              </span>
            )}
          </div>
          <p className="text-lg font-bold text-ink leading-snug">
            {isPaid ? `Studio ${studioPlan === 'pro' ? 'Créateur' : studioPlan === 'pro_plus' ? 'Pro' : studioPlan}` : 'Pas d\'abonnement'}
          </p>
          {!isPaid && (
            <p className="text-xs text-ink-muted mt-1">Free mode — débloque Studio Créateur pour les formats adaptés à ton compte, la library et les saves illimités</p>
          )}
        </div>

        {/* Actions abonnement */}
        {isPaid ? (<>
          <button onClick={gotoPortal} disabled={portalLoading}
            className="w-full py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-50 mb-2 flex items-center justify-center gap-2 hover:scale-[1.01]"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', color: 'white', boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
            {portalLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Ouverture…</span>
              </>
            ) : (
              <>
                <span>Factures et moyen de paiement</span>
                <span>→</span>
              </>
            )}
          </button>
          <button onClick={() => startCancelFlow(false)} disabled={portalLoading}
            className="w-full py-2 mb-2 text-[12px] text-ink-muted hover:text-ink underline underline-offset-2 disabled:opacity-60">
            Résilier mon abonnement
          </button>
        </>) : (
          <button onClick={() => { onClose(); goToStudioCheckout() }}
            className="w-full py-3 rounded-xl font-bold text-sm transition-all mb-2 flex items-center justify-center gap-2 hover:scale-[1.01]"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', color: 'white', boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
            🔓 Débloquer Studio Pro
          </button>
        )}
        {portalError && <p className="text-xs text-red-600 mt-1 text-center">⚠ {portalError}</p>}

        {isPaid && (
          <p className="text-[11px] text-ink-faint text-center mb-4 leading-snug">
            Stripe t'enverra un lien sécurisé par email pour accéder à tes factures et ta carte.
          </p>
        )}

        {/* Infos compte */}
        <div className="space-y-2.5 mb-5 pt-4 border-t border-border">
          <ProfileRow label="Email" value={user?.email} />
          {userProfile?.display_name && <ProfileRow label="Nom" value={userProfile.display_name} />}
          {memberSince && <ProfileRow label="Membre depuis" value={memberSince} />}
        </div>

        {/* ── Outils admin — regroupés ici (plus dans la barre du haut) ── */}
        {isAdmin && (
          <div className="mb-4 pt-4 border-t border-border">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2">🛠 Admin</p>
            <div className="flex flex-wrap gap-2">
              <StudioAdminLogsButton />
              <StudioAdminTopFormatsButton />
              <StudioAdminAdsButton />
              <StudioAdminSeoButton />
              <StudioAdminHubLink />
              <StudioAdminToggle currentPlan={studioPlan} />
              <StudioAdminVerticalToggle />
              <StudioAdminSimulateSportButton />
              <StudioAdminGuestToggle />
              <StudioAdminResetButton />
            </div>
          </div>
        )}

        {/* Revoir le tutoriel guidé */}
        <button onClick={() => { onClose(); try { window.dispatchEvent(new CustomEvent('studio:restart-tutorial')) } catch {} }}
          className="w-full py-2.5 rounded-lg text-xs font-bold text-ink-muted hover:text-orange transition-colors border border-border mb-2"
          style={{ borderColor: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
          🎓 Revoir le tutoriel
        </button>

        {/* Footer : sign out */}
        <button onClick={onSignOut}
          className="w-full py-2.5 rounded-lg text-xs font-bold text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors border border-border">
          Se déconnecter
        </button>
        </>)}
      </div>
    </div>
  )
}

function ProfileRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="text-ink-faint shrink-0">{label}</span>
      <span className="text-ink font-medium text-right break-all">{value || '—'}</span>
    </div>
  )
}

// Détection mode preview (?studio=paid-preview) — bypass backend
function isStudioPreview() {
  try {
    const sp = new URLSearchParams(window.location.search).get('studio')
    return sp === 'paid-preview' || sp === 'preview'
  } catch { return false }
}

export default function StudioPro({ onBack }) {
  // Expériences UI (agent retention-ux) : charge les variantes pour les
  // surfaces des connectés (home_*, paywall_*) et re-rend à leur arrivée.
  const [, forceUiExp] = useState(0)
  useEffect(() => { initUiExperiments(); return subscribeUiExperiments(() => forceUiExp(x => x + 1)) }, [])
  const auth = useAuth()
  const { user } = auth
  const preview = isStudioPreview()
  const [view, setView] = useState('home') // home | library | vault
  // "Revenir aux reels" depuis l'analyse de compte : retour à "Pour toi"
  // (l'analyse reste montée en fond et continue de poller).
  useEffect(() => {
    function goHome() { setView('home') }
    // Navigation vers les surfaces cles (CTA de la checklist d'activation).
    function goLibrary() { setView('library') }
    function goVideoAnalysis() { setView('analyse') } // section 'videos' par defaut
    function goAccountAnalysis() { setView('analyse') } // StudioAnalysisTab bascule sur 'account'
    window.addEventListener('studio:goto-home', goHome)
    window.addEventListener('studio:goto-library', goLibrary)
    window.addEventListener('studio:goto-video-analysis', goVideoAnalysis)
    window.addEventListener('studio:goto-account-analysis', goAccountAnalysis)
    // Deep-link du mail « analyse vidéo offerte » (?va=1) : ouvre direct
    // l'onglet Analyse où le rapport pré-généré attend l'utilisateur
    const _sp = new URLSearchParams(window.location.search)
    if (_sp.get('va') === '1') goVideoAnalysis()
    // Deep-link de la fiche publique /video/:id (CTA « Analyser ma vidéo ») :
    // ?goto=video ouvre l'onglet Analyse (Créateur/Pro+ payants → analyse directe).
    if (_sp.get('goto') === 'video') goVideoAnalysis()
    return () => {
      window.removeEventListener('studio:goto-home', goHome)
      window.removeEventListener('studio:goto-library', goLibrary)
      window.removeEventListener('studio:goto-video-analysis', goVideoAnalysis)
      window.removeEventListener('studio:goto-account-analysis', goAccountAnalysis)
    }
  }, [])
  const [profileOpen, setProfileOpen] = useState(false)
  // FICHE-CADEAU (?va_fiche=<analysis_id>&k=<kind>) : atterrissage du mail
  // « analyse offerte » free/past — l'analyse complète déverrouillée en
  // plein écran, paywall Pro+ sur le CTA « analyser une autre » (spec §4bis).
  const [vaFiche, setVaFiche] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search)
      return q.get('va_fiche') ? { id: q.get('va_fiche'), kind: q.get('k') || 'free' } : null
    } catch { return null }
  })
  function closeVaFiche() {
    setVaFiche(null)
    try {
      const u = new URL(window.location.href)
      u.searchParams.delete('va_fiche'); u.searchParams.delete('k')
      window.history.replaceState({}, '', u)
    } catch { /* noop */ }
  }
  // Deep link du mail J-3 (?extend=1) : ouvre direct l'écran de prolongation
  const [autoExtend] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('extend') === '1' } catch { return false }
  })
  useEffect(() => { if (autoExtend) setProfileOpen(true) }, [autoExtend])
  const [airtableReady, setAirtableReady] = useState(false)
  const airtableLoadedRef = useRef(false)
  const [copyState, setCopyState] = useState({})
  const [saveState, setSaveState] = useState({}) // format_id → save row id

  // Tracking : quelles vues ont déjà été montées au moins une fois.
  // Après mount, on garde les composants avec display:none (au lieu de
  // démonter) → leur state local est préservé → switch instantané sans re-fetch.
  const [mounted, setMounted] = useState({ home: true, library: false, vault: false, analyse: false, admin: false })
  const isAdminUser = user?.email === 'antonin.girardeau.pro@gmail.com'

  // ── Generic mode detection : si la session active a is_generic=true,
  // l'utilisateur n'a pas de compte Instagram lié. On cache l'onglet
  // "Analyse" (analyse de compte IG) puisque c'est inapplicable.
  const [isGenericSession, setIsGenericSession] = useState(false)
  useEffect(() => {
    if (preview) return
    let cancelled = false
    ;(async () => {
      try {
        const { data: sess } = await sb.auth.getSession()
        const token = sess?.session?.access_token
        if (!token) return
        const res = await fetch(`${API_URL}/api/studio/session`, { headers: { Authorization: `Bearer ${token}` } })
        const data = await res.json()
        if (cancelled) return
        if (data?.persona?.is_generic === true) setIsGenericSession(true)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [preview])

  // Si l'user est sur l'onglet analyse alors qu'on découvre qu'il est generic,
  // bascule sur Home pour pas le laisser sur un écran inutile.
  useEffect(() => {
    if (isGenericSession && view === 'analyse') setView('home')
  }, [isGenericSession, view])
  useEffect(() => {
    if (!mounted[view]) setMounted(m => ({ ...m, [view]: true }))
  }, [view, mounted])

  // PRE-FETCH des autres tabs en background : Home charge en priorité
  // (déjà mounted: true au render initial), puis après 800ms on mount les
  // autres tabs en display:none → leurs useEffect firent → données arrivent
  // → switch user devient instantané.
  useEffect(() => {
    const t = setTimeout(() => {
      setMounted(m => ({ ...m, library: true, vault: true, analyse: true }))
    }, 800)
    return () => clearTimeout(t)
  }, [])

  // Charge URLs MP4 fraîches au mount
  useEffect(() => {
    if (airtableLoadedRef.current) return
    airtableLoadedRef.current = true
    const safety = setTimeout(() => setAirtableReady(true), 25000)
    loadAllVideoUrls().then(() => { clearTimeout(safety); setAirtableReady(true) })
      .catch(() => { clearTimeout(safety); setAirtableReady(true) })
  }, [])

  async function authHeaders() {
    const { data } = await sb.auth.getSession()
    const token = data?.session?.access_token
    return token ? { 'Authorization': `Bearer ${token}` } : {}
  }

  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(null)
  // Mock saves en mode preview (pas de backend)
  const [mockSavesArr, setMockSavesArr] = useState([])

  // ── Re-scan : nouvel @ Instagram à analyser sans quitter Pro ──
  const [rescanOpen, setRescanOpen] = useState(false)
  const [rescanHandle, setRescanHandle] = useState('')
  const [rescanState, setRescanState] = useState({ launching: false, sessionId: null, step: null, pct: 0, error: null })
  const rescanPollRef = useRef(null)

  useEffect(() => {
    const openRescan = () => setRescanOpen(true)
    window.addEventListener('studio:open-rescan', openRescan)
    return () => window.removeEventListener('studio:open-rescan', openRescan)
  }, [])
  async function launchRescan() {
    try { window.dispatchEvent(new CustomEvent('studio:rescan-started')) } catch { /* tracker */ }
    const clean = (rescanHandle || '').trim().replace(/^@/, '')
    if (!clean) { setRescanState(s => ({ ...s, error: 'Tape un @ Instagram' })); return }
    setRescanState({ launching: true, sessionId: null, step: 'Démarrage', pct: 5, error: null })
    try {
      const h = await authHeaders()
      const res = await fetch(`${API_URL}/api/studio/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ ig_username: clean }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setRescanState(s => ({ ...s, sessionId: data.id, step: 'En cours…', pct: 15 }))
    } catch (e) {
      setRescanState({ launching: false, sessionId: null, step: null, pct: 0, error: e.message })
    }
  }

  // Polling rescan : toutes les 2.5s, update step + pct, et au done → refresh drop
  useEffect(() => {
    if (rescanPollRef.current) { clearInterval(rescanPollRef.current); rescanPollRef.current = null }
    if (!rescanState.sessionId) return
    let cancelled = false
    async function tick() {
      try {
        const h = await authHeaders()
        const res = await fetch(`${API_URL}/api/studio/session/${rescanState.sessionId}`, { headers: h })
        const data = await res.json()
        if (cancelled) return
        if (res.ok) {
          setRescanState(s => ({ ...s, step: data.current_step || s.step, pct: data.progress_pct || s.pct }))
          if (data.status === 'done') {
            clearInterval(rescanPollRef.current)
            // Hard reload pour récupérer la nouvelle session studio + persona
            // (fetchDrop est scopé dans ProHome, pas accessible ici)
            window.location.reload()
            return
          } else if (data.status === 'error') {
            clearInterval(rescanPollRef.current)
            setRescanState(s => ({ ...s, launching: false, error: data.error || 'Erreur scan' }))
          }
        }
      } catch {}
    }
    tick()
    rescanPollRef.current = setInterval(tick, 2500)
    return () => { cancelled = true; if (rescanPollRef.current) clearInterval(rescanPollRef.current) }
  }, [rescanState.sessionId])

  async function saveFormat(format) {
    if (!format?.format_id) {
      console.warn('[save] format sans format_id', format)
      return
    }
    setSaveError(null)
    console.log('[save] click', format.format_id, 'preview?', preview)

    // ★ OPTIMISTIC UPDATE : on toggle l'état avant l'appel API → feedback
    // visuel instantané. Si l'API échoue, on revert.
    const tempId = `temp-${Date.now()}`
    setSaveState(s => ({ ...s, [format.format_id]: tempId }))

    // ── MODE PREVIEW : toggle local sans appel backend ──
    if (preview) {
      const fakeId = `mock-save-${format.format_id}`
      setSaveState(s => ({ ...s, [format.format_id]: fakeId }))
      setMockSavesArr(arr => [...arr, { id: fakeId, format_id: format.format_id, format_data: format, status: 'saved', created_at: new Date().toISOString() }])
      setSaveSuccess('Format ajouté à Mes formats')
      setTimeout(() => setSaveSuccess(null), 2500)
      // Coche l'étape activation "sauvegarde" (même en preview/QA).
      try { window.dispatchEvent(new CustomEvent('studio:saves-changed')) } catch {}
      return
    }

    try {
      const h = await authHeaders()
      const res = await fetch(`${API_URL}/api/studio/saves`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ format_id: format.format_id, format_data: format }),
      })
      const data = await res.json()
      console.log('[save] response', res.status, data)
      if (!res.ok || data?.error) {
        const msg = data?.error || data?.message || `HTTP ${res.status}`
        // Revert l'optimistic update
        setSaveState(s => { const n = { ...s }; delete n[format.format_id]; return n })
        if (String(msg).includes('studio_saves') || String(msg).includes('schema cache') || String(msg).includes('PGRST205')) {
          setSaveError("La sauvegarde n'est pas activée (table studio_saves manquante). Applique la migration 028.")
        } else if (String(msg).includes('uuid')) {
          setSaveError("Format_id invalide (probablement un mock). Lance un vrai scan ?studio=1.")
        } else {
          setSaveError(`Erreur sauvegarde : ${msg}`)
        }
        console.error('[save]', msg)
        setTimeout(() => setSaveError(null), 6000)
        return
      }
      if (data?.id) {
        // Remplace l'ID temp par le vrai ID
        setSaveState(s => ({ ...s, [format.format_id]: data.id }))
        setSaveSuccess('Format ajouté à Mes formats')
        setTimeout(() => setSaveSuccess(null), 2000)
        // Notifie le coffre (ProVault) pour qu'il refetch et affiche le nouveau format.
        try { window.dispatchEvent(new CustomEvent('studio:saves-changed')) } catch {}
      }
    } catch (e) {
      console.error('[save]', e.message)
      // Revert
      setSaveState(s => { const n = { ...s }; delete n[format.format_id]; return n })
      setSaveError(`Erreur réseau : ${e.message}`)
      setTimeout(() => setSaveError(null), 6000)
    }
  }

  // ── Deep-link "Ajouter à ma bibliothèque" (/format/:id → ?format_id=X) ──
  // L'id a été capturé en localStorage à l'entrée. Pour un user payant : on
  // ajoute le format BRUT aux favoris (PAS d'adaptation auto : un format hors
  // niche se réinventerait mal). On ouvre "Mes formats" dessus, avec sa trame
  // source. L'adaptation reste un bouton in-app, choisi exprès. (Cas gratuit →
  // signup/essai, géré sur la surface gratuite de StudioPage.)
  const pendingAdaptRef = useRef(false)
  useEffect(() => {
    if (preview || pendingAdaptRef.current) return
    let pid = null
    try { pid = localStorage.getItem('studio_pending_adapt') } catch {}
    if (!pid) return
    const paid = ['pro', 'pro_plus', 'creator', 'agency'].includes(auth.studioPlan)
    if (!paid) return
    pendingAdaptRef.current = true
    let cancelled = false
    ;(async () => {
      try {
        // Format BRUT (source + beats), pas d'adaptation LLM. /api/format est public.
        const r = await fetch(`${API_URL}/api/format/${pid}`)
        const d = await r.json()
        if (!r.ok || !d.video) throw new Error(d.error || `HTTP ${r.status}`)
        if (cancelled) return
        const v = d.video
        const beats = Array.isArray(d.beats) ? d.beats : []
        const fd = {
          format_id: v.id,
          source: {
            createur: v.createur_nom, vues: v.vues, lien: v.lien,
            format_nom: v.format_nom, format_label: v.format_nom,
            hook_original: v.hook_audio_verbatim || '',
          },
          hook_template: v.hook_audio_template_fr || v.hook_audio_verbatim || '',
          beats_raw: beats,
          structure: beats,
        }
        await saveFormat(fd)                            // ajoute aux favoris (brut)
        try { localStorage.removeItem('studio_pending_adapt') } catch {}
        setView('vault')                                // ouvre Mes formats
      } catch (e) {
        console.warn('[pending-save]', e.message)
        pendingAdaptRef.current = false                 // retry possible au prochain render
      }
    })()
    return () => { cancelled = true }
  }, [auth.studioPlan, preview])

  async function unsaveFormat(format) {
    const saveId = saveState[format.format_id]
    if (!saveId) return
    if (preview) {
      setSaveState(s => { const n = { ...s }; delete n[format.format_id]; return n })
      setMockSavesArr(arr => arr.filter(s => s.id !== saveId))
      return
    }
    try {
      const h = await authHeaders()
      await fetch(`${API_URL}/api/studio/saves/${saveId}`, { method: 'DELETE', headers: h })
      setSaveState(s => { const n = { ...s }; delete n[format.format_id]; return n })
    } catch (e) { console.error('[unsave]', e.message) }
  }

  function copy(text, key) {
    navigator.clipboard.writeText(text || '').then(() => {
      setCopyState({ [key]: true })
      setTimeout(() => setCopyState({}), 2000)
    })
  }

  return (
    <div className="min-h-screen bg-bg">
      {/* Top nav Studio — responsive : tabs en 2e ligne sur mobile */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-border"
        style={{ background: 'rgba(255,251,245,0.92)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src={merlinFound} alt="Merlin" className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0"
                style={{ filter: 'drop-shadow(0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
              <span className="text-sm font-bold text-ink">Studio</span>
              {ACTIVE_VERTICAL ? (
                <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white"
                  style={{ background: ACTIVE_VERTICAL.accent }}>
                  {ACTIVE_VERTICAL.chip}
                </span>
              ) : (
                <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  Pro
                </span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-1">
              <NavTab active={view === 'home'} onClick={() => setView('home')}>Pour toi</NavTab>
              <NavTab active={view === 'library'} onClick={() => setView('library')} dataTour="tab-library">Découvrir</NavTab>
              <NavTab active={view === 'vault'} onClick={() => setView('vault')}>Mes formats</NavTab>
              {!isGenericSession && <NavTab active={view === 'analyse'} onClick={() => setView('analyse')} highlight dataTour="tab-analyse">Analyse</NavTab>}
              {isAdminUser && <NavTab active={view === 'admin'} onClick={() => setView('admin')}>🛠 Admin</NavTab>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setRescanOpen(true)}
                className="text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap transition-all flex items-center gap-1.5 hover:scale-[1.03]"
                style={{
                  background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
                }}
                title="Re-scanner un autre compte Instagram">
                <span className="text-sm">↻</span>
                <span className="hidden sm:inline">Re-scanner</span>
              </button>
              {/* Outils admin déplacés dans le menu profil (avatar) pour désencombrer la barre */}
              {/* Avatar profil → ouvre modal compte/abonnement + admin + tuto */}
              <button onClick={() => setProfileOpen(true)}
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 shrink-0"
                style={{
                  background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(var(--merlin-violet-rgb, 124,58,237),0.30)',
                }}
                title="Profil & abonnement">
                <span className="text-xs font-bold">
                  {(user?.email?.[0] || '?').toUpperCase()}
                </span>
              </button>
            </div>
          </div>
          {/* Tabs mobile : 2e ligne, scrollable */}
          <div className="md:hidden flex items-center gap-1 mt-2 -mx-1 px-1 overflow-x-auto">
            <NavTab active={view === 'home'} onClick={() => setView('home')}>Pour toi</NavTab>
            <NavTab active={view === 'library'} onClick={() => setView('library')} dataTour="tab-library">Découvrir</NavTab>
            <NavTab active={view === 'vault'} onClick={() => setView('vault')}>Mes formats</NavTab>
            {!isGenericSession && <NavTab active={view === 'analyse'} onClick={() => setView('analyse')} highlight dataTour="tab-analyse">Analyse</NavTab>}
            {isAdminUser && <NavTab active={view === 'admin'} onClick={() => setView('admin')}>🛠 Admin</NavTab>}
          </div>
        </div>
      </div>

      {/* ══ Modal Re-scan : nouvel @ Instagram à analyser ══ */}
      {rescanOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => !rescanState.launching && setRescanOpen(false)}
          style={{ background: 'rgba(15,10,26,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-0.5">Re-scanner</p>
                <h2 className="text-lg font-bold text-ink leading-tight">Nouvelle analyse de profil</h2>
              </div>
              {!rescanState.launching && (
                <button onClick={() => setRescanOpen(false)} className="text-lg text-ink-muted hover:text-ink leading-none p-1">×</button>
              )}
            </div>

            {!rescanState.sessionId ? (
              <>
                <label className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5 block">
                  Compte Instagram
                </label>
                <div className="flex items-center gap-1 bg-bg/70 rounded-lg px-3 py-2.5 mb-3 border border-border focus-within:border-orange/40">
                  <span className="text-ink-faint text-base">@</span>
                  <input
                    type="text"
                    value={rescanHandle}
                    onChange={(e) => setRescanHandle(e.target.value)}
                    placeholder="ton_compte_instagram"
                    autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    disabled={rescanState.launching}
                    className="bg-transparent flex-1 outline-none text-ink placeholder-ink-faint text-sm"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !rescanState.launching) launchRescan() }}
                    autoFocus />
                </div>
                <button onClick={launchRescan} disabled={rescanState.launching || !rescanHandle.trim()}
                  className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 hover:scale-[1.01]"
                  style={{
                    background: rescanState.launching || !rescanHandle.trim() ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                    boxShadow: rescanState.launching || !rescanHandle.trim() ? 'none' : '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
                  }}>
                  {rescanState.launching ? 'Lancement…' : 'Lancer l\'analyse →'}
                </button>
                {rescanState.error && <p className="text-xs text-red-600 mt-3 text-center">⚠ {rescanState.error}</p>}
                <p className="text-[11px] text-ink-faint mt-3 text-center">~90s · Remplace la persona actuelle</p>
              </>
            ) : (
              <div className="py-2">
                <p className="text-xs text-ink-muted text-center mb-3">{rescanState.step || 'Analyse en cours…'}</p>
                <div className="w-full h-1.5 bg-ink/10 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-[width]"
                    style={{
                      width: `${rescanState.pct}%`,
                      background: 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                    }} />
                </div>
                <p className="text-[10px] text-ink-faint text-right font-mono">{Math.round(rescanState.pct)} %</p>
                {rescanState.error && <p className="text-xs text-red-600 mt-3 text-center">⚠ {rescanState.error}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ Modal Profil & abonnement ══ */}
      {profileOpen && (
        <StudioProfileModal
          user={user}
          userProfile={auth.userProfile}
          studioPlan={auth.studioPlan}
          autoRetention={autoExtend}
          onClose={() => setProfileOpen(false)}
          onSignOut={async () => {
            if (!confirm('Te déconnecter de Studio ?')) return
            await auth.signOut()
            window.location.replace('/')
          }}
        />
      )}

      {/* Onboarding d'activation : séquence de bienvenue au 1er passage.
          Le CTA final ramène sur "Pour toi" et déploie le 1er format. */}
      <StudioOnboarding onOpenFirstFormat={() => {
        setView('home')
        // setView est async côté render : on laisse ProHome (re)devenir visible
        // avant de demander l'ouverture du 1er format.
        setTimeout(() => { try { window.dispatchEvent(new CustomEvent('studio:open-first-format')) } catch {} }, 60)
      }} />

      {/* Modal upgrade (depuis profil ou admin toggle 'free') */}
      <StudioUpgradeModal />
      {vaFiche && <VaGiftFiche analysisId={vaFiche.id} kind={vaFiche.kind} onClose={closeVaFiche} />}
      <StudioAnalysisPaywallModal />
      {/* Fiche complète d'un format (écoute studio:open-format-detail) */}
      <FormatDetailModal />
      {/* Démo du tuto : une vraie analyse vidéo (fixture réelle) */}
      <TourVideoAnalysisDemo />
      {/* Retour du lien "mot de passe oublié" */}
      <PasswordRecoveryModal />

      {/* Toast d'erreur (save fail, table manquante, etc.) */}
      {saveError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-32px)]">
          <div className="rounded-xl px-4 py-3 shadow-lg flex items-start gap-2"
            style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b' }}>
            <span className="shrink-0">⚠</span>
            <p className="text-sm leading-snug flex-1">{saveError}</p>
            <button onClick={() => setSaveError(null)} className="text-xs underline shrink-0">Fermer</button>
          </div>
        </div>
      )}

      {/* Toast de succès */}
      {saveSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-32px)]">
          <div className="rounded-xl px-4 py-3 shadow-lg flex items-center gap-2"
            style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534' }}>
            <span className="shrink-0">✓</span>
            <p className="text-sm font-bold leading-snug flex-1">{saveSuccess}</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Les 3 vues sont rendues en parallèle (cachées via display:none quand
            pas actives) → state local préservé → switch instantané sans re-fetch.
            Seules les vues "déjà visitées" sont montées (lazy mount). */}
        <div style={{ display: view === 'home' ? 'block' : 'none' }}>
          {mounted.home && (
            <ProHome
              user={user}
              airtableReady={airtableReady}
              saveState={saveState}
              saveFormat={saveFormat}
              unsaveFormat={unsaveFormat}
              copy={copy}
              copyState={copyState}
              setView={setView}
            />
          )}
        </div>
        <div style={{ display: view === 'library' ? 'block' : 'none' }}>
          {mounted.library && (
            <ProLibrary
              airtableReady={airtableReady}
              saveState={saveState}
              saveFormat={saveFormat}
              unsaveFormat={unsaveFormat}
              copy={copy}
              copyState={copyState}
            />
          )}
        </div>
        <div style={{ display: view === 'vault' ? 'block' : 'none' }}>
          {mounted.vault && (
            <ProVault
              airtableReady={airtableReady}
              copy={copy}
              copyState={copyState}
              preview={preview}
              mockSaves={mockSavesArr}
              onMockUnsave={(saveId) => {
                setMockSavesArr(arr => arr.filter(s => s.id !== saveId))
                setSaveState(s => {
                  const n = { ...s }
                  for (const k of Object.keys(n)) if (n[k] === saveId) delete n[k]
                  return n
                })
              }}
              onUnsave={(saveId) => {
                setSaveState(s => {
                  const n = { ...s }
                  for (const k of Object.keys(n)) if (n[k] === saveId) delete n[k]
                  return n
                })
              }}
            />
          )}
        </div>
        {/* ═══ Vue ANALYSE — mes vidéos + mon compte (Pro+ gated) ═══ */}
        <div style={{ display: view === 'analyse' ? 'block' : 'none' }}>
          {mounted.analyse && <StudioAnalysisTab />}
        </div>
        {/* ═══ Vue ADMIN (logs users) — visible que pour antonin ═══ */}
        {isAdminUser && (
          <div style={{ display: view === 'admin' ? 'block' : 'none' }}>
            {mounted.admin && <StudioAdminPanel />}
          </div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// STUDIO ADMIN PANEL — liste users + sessions + accès aux logs
// Visible uniquement pour antonin.girardeau.pro@gmail.com
// ═══════════════════════════════════════════════════════════════
export function StudioAdminPanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null) // user
  const [sessions, setSessions] = useState([])
  const [sessLoading, setSessLoading] = useState(false)
  const [logs, setLogs] = useState(null)
  const [logsLoading, setLogsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all') // all | pro | active

  async function authH() {
    const { data: sess } = await sb.auth.getSession()
    const token = sess?.session?.access_token
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  useEffect(() => {
    (async () => {
      try {
        const h = await authH()
        const r = await fetch(`${API_URL}/api/admin/studio/users`, { headers: h })
        const d = await r.json()
        setData(d)
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    })()
  }, [])

  async function openUser(u) {
    setSelected(u)
    setSessions([])
    setLogs(null)
    setSessLoading(true)
    try {
      const h = await authH()
      const r = await fetch(`${API_URL}/api/admin/studio/user/${u.id}/sessions`, { headers: h })
      const d = await r.json()
      setSessions(d.sessions || [])
    } catch (e) { console.error(e) }
    finally { setSessLoading(false) }
  }

  async function openSessionLogs(sessionId) {
    setLogs({ session_id: sessionId, items: null })
    setLogsLoading(true)
    try {
      const h = await authH()
      const r = await fetch(`${API_URL}/api/studio/logs/${sessionId}`, { headers: h })
      const d = await r.json()
      setLogs({ session_id: sessionId, items: d.logs || [] })
    } catch (e) { console.error(e) }
    finally { setLogsLoading(false) }
  }

  const users = (data?.users || []).filter(u => {
    if (filter === 'pro' && !u.studio_plan) return false
    if (filter === 'active' && u.sessions_count === 0) return false
    if (search && !`${u.email} ${u.display_name || ''} ${u.last_handle || ''}`.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" /></div>

  return (
    <div className="space-y-4">
      {/* Stats globales */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatBox label="Users" value={data?.count || 0} />
        <StatBox label="Pro" value={data?.pro_count || 0} accent="orange" />
        <StatBox label="Actifs" value={data?.with_sessions_count || 0} accent="purple" />
      </div>

      {/* Search + filters */}
      <div className="flex gap-2">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 email, nom, @handle..."
          className="flex-1 text-sm px-3 py-2 rounded-lg border border-border bg-white outline-none focus:border-orange/40" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="text-xs font-bold px-3 py-2 rounded-lg border border-border bg-white outline-none">
          <option value="all">Tous</option>
          <option value="pro">Pro only</option>
          <option value="active">Avec sessions</option>
        </select>
      </div>

      {/* Liste users */}
      <div className="space-y-1.5">
        {users.map(u => (
          <button key={u.id} onClick={() => openUser(u)}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors border border-border bg-white hover:border-orange/30 hover:bg-orange/[0.02]">
            <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-bold text-white"
              style={{ background: u.studio_plan ? 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' : 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' }}>
              {(u.email?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-ink truncate">{u.email}</p>
                {u.studio_plan && (
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange/15 text-orange shrink-0">
                    {u.studio_plan}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-ink-faint">
                {u.sessions_count} session{u.sessions_count > 1 ? 's' : ''}
                {u.last_handle && <> · dernier <b>@{u.last_handle}</b></>}
                {u.last_session_at && <> · {new Date(u.last_session_at).toLocaleDateString('fr-FR')}</>}
              </p>
            </div>
            <span className="text-ink-faint text-xs shrink-0">→</span>
          </button>
        ))}
        {users.length === 0 && <p className="text-xs text-ink-faint text-center py-6">Aucun user matchant</p>}
      </div>

      {/* Modal user → sessions */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => { setSelected(null); setLogs(null) }}
          style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-0.5">User</p>
                <p className="text-sm font-bold text-ink truncate">{selected.email}</p>
                <p className="text-[11px] text-ink-faint">
                  {selected.studio_plan || 'Free'} · membre depuis {new Date(selected.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <button onClick={() => { setSelected(null); setLogs(null) }}
                className="text-2xl text-ink-muted hover:text-ink leading-none p-1">×</button>
            </div>

            {/* Sessions */}
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2 mt-3">
              Sessions Studio · {sessions.length}
            </p>
            {sessLoading ? (
              <p className="text-xs text-ink-faint">Chargement…</p>
            ) : sessions.length === 0 ? (
              <p className="text-xs text-ink-faint italic">Aucune session</p>
            ) : (
              <div className="space-y-1.5">
                {sessions.map(s => (
                  <button key={s.id} onClick={() => openSessionLogs(s.id)}
                    className="w-full flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-white hover:border-orange/30 text-left text-xs">
                    <div className="min-w-0">
                      <p className="font-bold text-ink">@{s.ig_username}</p>
                      <p className="text-[10px] text-ink-faint">
                        {s.status} · {new Date(s.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    <span className="text-[10px] font-mono text-ink-faint shrink-0">
                      {s.id.substring(0, 8)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Logs de la session sélectionnée */}
            {logs && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">
                    Logs pipeline · {logs.session_id?.substring(0, 8)}
                  </p>
                  <button onClick={() => setLogs(null)} className="text-[10px] text-ink-muted hover:text-ink underline">Fermer</button>
                </div>
                {logsLoading ? (
                  <p className="text-xs text-ink-faint">Chargement…</p>
                ) : !logs.items?.length ? (
                  <p className="text-xs text-ink-faint italic">Aucun log pour cette session (avant l'instrumentation)</p>
                ) : (
                  <div className="space-y-2 text-[11px] font-mono">
                    {logs.items.map((l, i) => (
                      <div key={i} className="p-2 rounded bg-ink/[0.03] border border-border/40">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-bold text-purple-700">{l.step}</span>
                          <span className="text-ink-faint">{l.duration_ms}ms</span>
                        </div>
                        {l.error && <p className="text-red-600">⚠ {l.error}</p>}
                        {l.context?.lien && <p className="text-ink-muted truncate">📷 {l.context.lien}</p>}
                        {l.context?.format_nom && <p className="text-ink-muted">📋 {l.context.format_nom}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function StatBox({ label, value, accent }) {
  const colors = accent === 'orange'
    ? { bg: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', txt: 'var(--merlin-accent, #fe8204)' }
    : accent === 'purple'
      ? { bg: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)', txt: 'var(--merlin-violet, #7c3aed)' }
      : { bg: 'rgba(0,0,0,0.04)', txt: 'rgb(74,68,88)' }
  return (
    <div className="rounded-lg p-3" style={{ background: colors.bg }}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-0.5">{label}</p>
      <p className="text-2xl font-bold" style={{ color: colors.txt }}>{value}</p>
    </div>
  )
}

function PersonaCol({ label, items, compact }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">{label}</p>
      {compact ? (
        <p className="text-xs text-ink leading-snug">
          {items.map((v, i) => (
            <span key={i}>« {v} »{i < items.length - 1 ? ' · ' : ''}</span>
          ))}
        </p>
      ) : (
        <ul className="text-xs text-ink space-y-1">
          {items.slice(0, 4).map((v, i) => (
            <li key={i} className="leading-snug flex gap-1.5">
              <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-orange" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function NavTab({ active, onClick, children, highlight = false, dataTour }) {
  if (highlight) {
    return (
      <button onClick={onClick} data-tour={dataTour}
        className="relative text-xs px-3 py-1.5 rounded-md font-bold transition-all flex items-center gap-1 whitespace-nowrap text-white hover:scale-[1.03] active:scale-[0.97]"
        style={{
          background: active
            ? 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))'
            : 'linear-gradient(135deg, var(--merlin-violet, #7c3aed) 0%, var(--merlin-violet-2, #6d28d9) 100%)',
          boxShadow: active
            ? '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.45), inset 0 1px 0 rgba(255,255,255,0.15)'
            : '0 2px 8px rgba(var(--merlin-violet-rgb, 124,58,237),0.30)',
        }}>
        <span className="text-[10px]">✨</span>
        {children}
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
          style={{ background: '#fdba74', boxShadow: '0 0 6px #fb923c' }} />
      </button>
    )
  }
  return (
    <button onClick={onClick} data-tour={dataTour}
      className="text-xs px-3 py-1.5 rounded-md font-bold transition-colors"
      style={{
        background: active ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)' : 'transparent',
        color: active ? 'var(--merlin-accent, #fe8204)' : 'rgb(107,104,98)',
      }}>
      {children}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIEW : HOME — drop hebdo + raccourci coffre
// ═══════════════════════════════════════════════════════════════

// Bulle (?) sur la carte note : les insights compte au survol / au clic
// (remplace l'ancien bloc Insights déroulant, demande Antonin 08/07).
export function InsightsBubble({ persona }) {
  const [open, setOpen] = useState(false)
  const hoverAt = useRef(0)
  return (
    <div className="absolute top-2.5 right-2.5 z-20"
      onMouseEnter={() => { hoverAt.current = Date.now(); setOpen(true) }} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => { if (Date.now() - hoverAt.current < 500) return; setOpen(o => !o) }} aria-label="Insights compte"
        className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors"
        style={{ background: open ? 'var(--merlin-violet, #7c3aed)' : 'rgba(var(--merlin-violet-rgb, 124,58,237),0.12)', color: open ? '#fff' : 'var(--merlin-violet, #7c3aed)' }}>?</button>
      {open && (
        <div className="absolute right-0 top-6 w-[290px] max-w-[82vw] rounded-xl p-3.5 text-left shadow-xl"
          style={{ background: '#fff', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)', boxShadow: '0 10px 30px rgba(var(--merlin-violet-rgb, 124,58,237),0.18)' }}>
          <p className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: 'var(--merlin-violet, #7c3aed)' }}>✨ Insights compte</p>
          <p className="text-[12px] font-bold text-ink leading-snug mb-2">{persona.niche_fine}</p>
          {persona.audience_cible && (
            <p className="text-[11px] text-ink-muted leading-snug mb-1.5"><b className="text-ink">Audience</b> · {persona.audience_cible}</p>
          )}
          {persona.ton_voix && (
            <p className="text-[11px] text-ink-muted leading-snug mb-1.5"><b className="text-ink">Voix</b> · <span className="italic">{persona.ton_voix}</span></p>
          )}
          {persona.douleurs?.length > 0 && (
            <p className="text-[11px] text-ink-muted leading-snug mb-1"><b className="text-ink">Douleurs</b> · {persona.douleurs.slice(0, 3).join(' · ')}</p>
          )}
          {persona.desirs?.length > 0 && (
            <p className="text-[11px] text-ink-muted leading-snug"><b className="text-ink">Désirs</b> · {persona.desirs.slice(0, 3).join(' · ')}</p>
          )}
        </div>
      )}
    </div>
  )
}

function ProHome({ user, airtableReady, saveState, saveFormat, unsaveFormat, copy, copyState, setView }) {
  // Activation payante : l'user qui a payé atterrit bien sur le produit.
  useEffect(() => { try { studioEvents.studioProHomeShown() } catch {} }, [])
  // Signal d'ouverture du 1er format : incrémenté à chaque demande
  // 'studio:open-first-format' (onboarding CTA / checklist). La 1ère
  // ProFormatCard l'écoute pour déplier son <details> + scroller dessus.
  const [openFirstSignal, setOpenFirstSignal] = useState(0)
  useEffect(() => {
    function onOpenFirst() { setOpenFirstSignal(n => n + 1) }
    window.addEventListener('studio:open-first-format', onOpenFirst)
    return () => window.removeEventListener('studio:open-first-format', onOpenFirst)
  }, [])
  const [drop, setDrop] = useState(null)
  // RITUEL QUOTIDIEN (phase 1 rétention 2026-07-07) : streak, drops manqués,
  // compte à rebours du prochain drop. Best-effort, la home vit sans.
  const [rituel, setRituel] = useState(null)
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data } = await sb.auth.getSession()
        const token = data?.session?.access_token
        if (!token) return
        const r = await fetch(`${API_URL}/api/studio/rituel`, { headers: { Authorization: `Bearer ${token}` } })
        if (r.ok && alive) setRituel(await r.json())
      } catch { /* silencieux */ }
    })()
    return () => { alive = false }
  }, [])
  // Tuto en contexte : ouvre la VRAIE fiche du 1er format du jour pour
  // montrer où vivent l'analyse et les exemples perso.
  const dropRef = useRef(null)
  dropRef.current = drop
  useEffect(() => {
    function onTourOpen() {
      const f = (dropRef.current || [])[0]
      if (f) { try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format: f, idx: 0 } })) } catch { /* no-op */ } }
    }
    window.addEventListener('studio:tour-open-first-format', onTourOpen)
    return () => window.removeEventListener('studio:tour-open-first-format', onTourOpen)
  }, [])
  const [persona, setPersona] = useState(null)
  const [savesCount, setSavesCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  async function authH() {
    const { data: sess } = await sb.auth.getSession()
    const token = sess?.session?.access_token
    return token ? { 'Authorization': `Bearer ${token}` } : {}
  }

  // Précharge les fiches des formats "Pour toi" : l'appel déclenche la
  // génération côté serveur (cache 7 j) → au clic, l'analyse est instantanée.
  // Fire-and-forget, séquencé pour ne pas saturer Claude.
  useEffect(() => {
    const keys = (drop || []).map(f => f?.source?.airtable_id).filter(Boolean)
    if (!keys.length) return
    let alive = true
    ;(async () => {
      const h = await authH()
      for (const key of keys.slice(0, 3)) {
        if (!alive) return
        try { await fetch(`${API_URL}/api/studio/fiche?video=${encodeURIComponent(key)}`, { headers: h }) } catch { /* warm-up only */ }
      }
    })()
    return () => { alive = false }
  }, [(drop || []).map(f => f?.source?.airtable_id).join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchDrop(targetOffset = 0) {
    setRefreshing(true)
    try {
      const h = await authH()
      const res = await fetch(`${API_URL}/api/studio/weekly?offset=${targetOffset}&count=3`, { headers: h })
      const data = await res.json()
      setDrop(data.drop || [])
      setPersona(data.persona || null)
      setHasMore(!!data.hasMore)
      setOffset(targetOffset)
    } finally {
      setRefreshing(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      await fetchDrop(0)
      // load saves count
      const h = await authH()
      const sR = await fetch(`${API_URL}/api/studio/saves`, { headers: h })
      const s = await sR.json()
      if (!cancelled) setSavesCount((s.saves || []).length)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function rotateDrop() {
    // Remplace les 3 actuels par les 3 suivants. Si on est en fin de liste,
    // boucle au début (offset 0).
    const nextOffset = hasMore ? offset + 3 : 0
    await fetchDrop(nextOffset)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Bandeau avis discret (2e connexion + 5 min), tout en haut */}
      <AvisBanner sourcePage="pro_home" />
      {/* Frise « Améliore ton compte en 4 étapes ». Se replie quand tout est fait. */}
      <ActivationChecklist onOpenFirstFormat={() => setOpenFirstSignal(n => n + 1)} />

      {/* Hero : la note d'abord (le miroir) — titre porté par la carte score */}

      {/* Score /100 — le radar du compte, visible aussi pour les abonnés
          (pas juste en free/LP). Affiché seulement si le score est calculé.
          Le CTA "améliorer mon compte" envoie vers l'onglet Analyse (où vit
          l'analyse de compte détaillée), pas vers un paywall. */}
      {persona?.score?.dimensions?.length >= 3 && (
        <div data-tour="score" className="-mt-4 relative">
          <AccountScore compact score={persona.score} username={persona.ig_username} onCta={() => { setView('analyse'); try { window.dispatchEvent(new CustomEvent('studio:goto-account-analysis')) } catch {} }} />
          {persona.niche_fine && <InsightsBubble persona={persona} />}
        </div>
      )}


      {/* Drop (le format du jour est injecté en #1 par getWeeklyDrop) */}
      {drop?.length > 0 ? (
        <>
          {/* PENDANT TON ABSENCE : FOMO rétroactif qui récompense le retour.
              Affiché seulement si ≥2 drops manqués. */}
          {rituel?.days_missed >= 2 && rituel?.missed_formats?.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-violet-rgb, 124,58,237),0.08), rgba(var(--merlin-violet-rgb, 124,58,237),0.03))', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}>
              <p className="text-[13px] font-bold text-ink">
                🌙 Pendant ton absence, <span style={{ color: 'var(--merlin-violet, #7c3aed)' }}>{rituel.days_missed} drops sont passés</span>. J'en ai repêché {rituel.missed_formats.length} pour toi :
              </p>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                {rituel.missed_formats.map((m, i) => (
                  <button key={i} type="button"
                    onClick={() => {
                      try { studioEvents.tabClicked?.({ tab: 'missed_drop_opened', source_page: 'pro_home' }) } catch {}
                      try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format: { source: { airtable_id: m.airtable_id, format_nom: m.format_nom, vues: m.vues, createur_nom: m.createur_nom } }, idx: i } })) } catch {}
                    }}
                    className="flex-1 text-left px-3 py-2.5 rounded-xl bg-white transition-transform hover:scale-[1.01]"
                    style={{ border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.20)' }}>
                    <p className="text-[12.5px] font-bold text-ink leading-snug truncate">{m.format_nom}</p>
                    <p className="text-[11px] text-ink-muted mt-0.5">{m.vues >= 1e6 ? `${(m.vues / 1e6).toFixed(1)}M` : `${Math.round((m.vues || 0) / 1e3)}k`} vues{m.drop_date ? ` · ${m.drop_date.slice(8, 10)}/${m.drop_date.slice(5, 7)}` : ''}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="pt-2">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-bold text-ink leading-tight">
                Le drop <span className="text-orange">du jour</span> 🔥
              </h2>
              {/* Série + prochain drop : le rendez-vous quotidien est DATÉ */}
              <div className="flex items-center gap-2 pb-1">
                {rituel?.streak >= 2 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-extrabold"
                    style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: '#d97206', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}
                    title="Jours de suite où tu es venu chercher ton format">
                    🔥 {rituel.streak} jours de suite
                  </span>
                )}
                {rituel?.next_drop_min > 0 && (
                  <span className="text-[11.5px] font-bold" style={{ color: 'var(--merlin-accent, #fe8204)' }}>
                    ⏳ nouveau drop dans {Math.floor(rituel.next_drop_min / 60)}h{String(rituel.next_drop_min % 60).padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-ink-muted mt-1.5">
              Les meilleurs formats pour <b className="text-ink">TON</b> compte.
            </p>
          </div>
          <div data-tour="formats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {drop.map((f, idx) => (
              <ProDropCard
                key={f.format_id || idx}
                format={f}
                idx={idx + offset}
                airtableReady={airtableReady}
                isSaved={!!saveState[f.format_id]}
                onSave={() => saveFormat(f)}
                onUnsave={() => unsaveFormat(f)}
              />
            ))}
          </div>

          {/* Bouton rotation */}
          <div className="text-center pt-2">
            <button onClick={rotateDrop} disabled={refreshing}
              className="px-5 py-2.5 rounded-xl bg-white border border-border hover:border-orange/40 text-sm font-bold text-ink transition-colors inline-flex items-center gap-2 disabled:opacity-50">
              <span className={refreshing ? 'animate-spin' : ''}>↻</span>
              {refreshing ? 'Merlin cherche 3 autres formats…' : hasMore ? 'Voir 3 autres formats' : 'Recommencer la rotation'}
            </button>
            {refreshing && <p className="text-[11px] text-ink-faint mt-2 italic">~10 secondes</p>}
          </div>
        </>
      ) : (
        <div className="text-center py-16 rounded-2xl bg-white border border-border">
          <img src={merlinImg} alt="" className="w-14 h-14 mx-auto mb-3 opacity-60" />
          <p className="text-sm text-ink-muted mb-2">Aucun format disponible.</p>
          <p className="text-xs text-ink-faint">Lance d'abord une analyse de ton compte Instagram pour personnaliser ta sélection.</p>
        </div>
      )}


      {/* Coffre + Bibliothèque CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
        <button onClick={() => setView('vault')}
          className="text-left p-5 rounded-2xl bg-white border border-border hover:border-orange/30 transition-colors group">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Mes formats</p>
            <span className="text-orange opacity-0 group-hover:opacity-100 transition-opacity">→</span>
          </div>
          <p className="text-lg font-bold text-ink">
            {savesCount} format{savesCount > 1 ? 's' : ''} sauvegardé{savesCount > 1 ? 's' : ''}
          </p>
          <p className="text-xs text-ink-muted mt-1">Tes brouillons, scripts en cours, publications.</p>
        </button>

        <button onClick={() => setView('library')}
          className="text-left p-5 rounded-2xl bg-white border border-border hover:border-orange/30 transition-colors group">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Explorer la bibliothèque</p>
            <span className="text-orange opacity-0 group-hover:opacity-100 transition-opacity">→</span>
          </div>
          <p className="text-lg font-bold text-ink">Le top 1% des formats viraux</p>
          <p className="text-xs text-ink-muted mt-1">Filtre par type, mécanique virale, durée.</p>
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIEW : LIBRARY — scroll + filtres
// ═══════════════════════════════════════════════════════════════
function ProLibrary({ airtableReady, saveState, saveFormat, unsaveFormat, copy, copyState }) {
  // Filtres simplifiés (2026-06-11, demande produit) : niche + tranche de
  // vues uniquement. Types/mécanique/durée retirés : trop de chips, l'user
  // filtrait rarement et la grille devenait un mur de boutons.
  const [filtersOpen, setFiltersOpen] = useState(false) // menu déroulant Filtres (14/07, demande Antonin : tous les filtres regroupés)
  const [vues, setVues] = useState(null)
  const [difficulte, setDifficulte] = useState(null) // ultra | facile | difficile
  const [frOnly, setFrOnly] = useState(false)        // contenus FR uniquement
  const [recentOnly, setRecentOnly] = useState(false) // publiés < 7 jours
  const [niches, setNiches] = useState([])  // niches sélectionnées
  const [availableNiches, setAvailableNiches] = useState([])  // liste depuis API
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  // Matching monde (12/07) : le backend sert d'abord les formats de la
  // macro-niche du compte ; allWorlds = toggle « voir tous les mondes ».
  const [macro, setMacro] = useState(null)
  const [allWorlds, setAllWorlds] = useState(false)
  // Scroll infini (retour Antonin 13/07 : « ne charge pas en bas de page ») :
  // la sentinelle sous la grille déclenche loadMore dès qu'elle est visible.
  const sentinelRef = useRef(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMoreRef.current?.()
    }, { rootMargin: '600px' })
    obs.observe(el)
    return () => obs.disconnect()
  }, [results.length, total])
  const loadMoreRef = useRef(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 400)
    return () => clearTimeout(id)
  }, [query])

  // Charge la liste des niches dispos une fois (au mount)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: sess } = await sb.auth.getSession()
        const token = sess?.session?.access_token
        const h = token ? { 'Authorization': `Bearer ${token}` } : {}
        const res = await fetch(`${API_URL}/api/studio/library/niches`, { headers: h })
        const data = await res.json()
        if (!cancelled) setAvailableNiches(data.niches || [])
      } catch (e) { /* silent */ }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const { data: sess } = await sb.auth.getSession()
        const token = sess?.session?.access_token
        const h = token ? { 'Authorization': `Bearer ${token}` } : {}
        const res = await fetch(`${API_URL}/api/studio/library`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...h },
          body: JSON.stringify({
            query: debouncedQuery,
            filters: { niches, vues, difficulte, fr: frOnly, recent: recentOnly, allWorlds },
            limit: 30,
            offset: 0,
          }),
        })
        const data = await res.json()
        if (cancelled) return
        setResults(data.results || [])
        setTotal(data.total || 0)
        setMacro(data.macro || null)
        setOffset(30)
      } catch (e) {
        console.error('[library]', e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [debouncedQuery, niches, vues, difficulte, frOnly, recentOnly, allWorlds])

  loadMoreRef.current = () => { if (!loading && results.length < total) loadMore() }
  async function loadMore() {
    if (loading || results.length >= total) return
    setLoading(true)
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const h = token ? { 'Authorization': `Bearer ${token}` } : {}
      const res = await fetch(`${API_URL}/api/studio/library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({
          query: debouncedQuery,
          filters: { niches, vues, difficulte, fr: frOnly, recent: recentOnly, allWorlds },
          limit: 30,
          offset,
        }),
      })
      const data = await res.json()
      setResults(r => [...r, ...(data.results || [])])
      setOffset(o => o + 30)
    } finally {
      setLoading(false)
    }
  }

  function toggleNiche(key) {
    setNiches(n => n.includes(key) ? n.filter(x => x !== key) : [...n, key])
  }

  // Précharge l'analyse des 3 PREMIERS formats affichés : le clic sur une
  // quick win doit ouvrir une fiche déjà complète, zéro attente. Séquencé
  // (pas de rafale Claude), le cache serveur 7 j absorbe les répétitions.
  useEffect(() => {
    const keys = results.slice(0, 3).map(f => f?.source?.airtable_id).filter(Boolean)
    if (!keys.length) return
    let alive = true
    ;(async () => {
      try {
        const { data: sess } = await sb.auth.getSession()
        const token = sess?.session?.access_token
        if (!token) return
        for (const key of keys) {
          if (!alive) return
          try { await fetch(`${API_URL}/api/studio/fiche?video=${encodeURIComponent(key)}`, { headers: { Authorization: `Bearer ${token}` } }) } catch { /* warm-up only */ }
        }
      } catch { /* warm-up only */ }
    })()
    return () => { alive = false }
  }, [results.slice(0, 3).map(f => f?.source?.airtable_id).join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <div>
        {/* Totaux masqués (Antonin 13/08) : scroll infini du plus juste au
            moins juste, le compte n'apporte rien et casse l'impression. */}
        <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2">
          Bibliothèque
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-ink leading-tight">
          Explore tous les formats <span className="text-orange">analysés par Merlin</span>
        </h1>
      </div>

      {/* Search bar : recherche automatique en tapant (400 ms), Entrée =
          immédiat. Feedback visible : loupe, spinner pendant la recherche,
          croix pour effacer. */}
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted text-[15px] pointer-events-none">🔍</span>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setDebouncedQuery(query) } }}
          placeholder="Cherche un sujet (ex : marketing, sommeil, productivité…)"
          className="w-full pl-10 pr-10 py-3 rounded-xl bg-white border border-border outline-none focus:border-orange/40 transition-colors text-sm"
        />
        {loading ? (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
        ) : query ? (
          <button
            onClick={() => { setQuery(''); setDebouncedQuery('') }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-ink-muted/15 text-ink-muted text-[11px] font-bold leading-none"
            aria-label="Effacer la recherche"
          >✕</button>
        ) : null}
      </div>

      {/* Bandeau MONDE (matching 12/07) : dit clairement que la sélection
          part du monde du compte, avec le toggle « tous les mondes ». */}
      {(macro || allWorlds) && (
        <div className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5"
          style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),.07)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),.25)' }}>
          <p className="text-[12.5px] font-bold text-ink leading-snug">
            {allWorlds ? '🌍 Tous les secteurs confondus' : <>🎯 D'abord les formats de ton secteur : <span style={{ color: '#d97206' }}>{macro?.label}</span></>}
          </p>
          <button type="button" onClick={() => setAllWorlds(w => !w)}
            className="shrink-0 text-[11.5px] font-bold underline" style={{ color: '#d97206' }}>
            {allWorlds ? 'Revenir à mon secteur' : 'Voir tous les secteurs'}
          </button>
        </div>
      )}

      {/* Filtres : TOUS regroupés dans un menu déroulant (14/07, demande
          Antonin) — fermé par défaut, badge avec le nombre de filtres actifs. */}
      <div className="rounded-xl bg-white border border-border overflow-hidden">
        {(() => {
          const activeCount = niches.length + (vues ? 1 : 0) + (difficulte ? 1 : 0) + (frOnly ? 1 : 0)
          return (
            <button type="button" onClick={() => setFiltersOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3">
              <span className="text-[13px] font-bold text-ink">
                🎛 Filtres{activeCount > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-[10.5px] font-bold text-white" style={{ background: 'var(--merlin-accent, #fe8204)' }}>{activeCount}</span>}
              </span>
              <span className={`text-ink-muted text-[12px] transition-transform ${filtersOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>
          )
        })()}
        {filtersOpen && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          {availableNiches.length > 0 && (
            <FilterRow
              label="Niche"
              options={availableNiches.map(n => ({ key: n.nom, label: n.nom }))}
              selected={niches}
              onToggle={toggleNiche}
            />
          )}
          <FilterRow label="Vues" options={FILTER_VUES} selected={vues ? [vues] : []}
            onToggle={(k) => setVues(vues === k ? null : k)} single />
          <FilterRow label="Difficulté à reproduire"
            options={[
              { key: 'ultra', label: '⚡ Ultra facile' },
              { key: 'facile', label: '✓ Facile' },
              { key: 'difficile', label: '◆ Difficile' },
            ]}
            selected={difficulte ? [difficulte] : []}
            onToggle={(k) => setDifficulte(difficulte === k ? null : k)} single />
          {/* Filtre "Récents <7j" retiré (2026-07-02) : le corpus prod n'a que
              ~4 vidéos publiées <7j → filtre quasi toujours vide. Le badge
              🕐 Récent sur les cards reste, le backend garde filters.recent
              (à réactiver quand la découverte quotidienne aura rempli le stock). */}
          <FilterRow label="Contenus"
            options={[{ key: 'fr', label: '🇫🇷 En français' }]}
            selected={frOnly ? ['fr'] : []}
            onToggle={() => setFrOnly(v => !v)} />
        </div>
        )}
      </div>

      {/* Results */}
      {loading && results.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
          <p className="text-[12.5px] text-ink-muted">{debouncedQuery ? `Recherche de « ${debouncedQuery} »…` : 'Chargement des formats…'}</p>
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white border border-border">
          <p className="text-sm text-ink font-bold mb-1">{debouncedQuery ? `Aucun format pour « ${debouncedQuery} »` : 'Aucun format trouvé'}</p>
          <p className="text-sm text-ink-muted">Essaie un mot plus large (ex : « vues », « argent », « sport ») ou retire des filtres.</p>
        </div>
      ) : (
        <>
          {/* Totaux masqués (13/08) : on ne garde que le rappel de recherche
              et le spinner. Le flux est infini, du plus juste au moins juste. */}
          {(debouncedQuery || loading) && (
            <p className="text-[12px] text-ink-muted -mb-1">
              {debouncedQuery ? <>Résultats pour « <span className="font-bold text-ink">{debouncedQuery}</span> »</> : null}
              {loading && <span className="ml-2 inline-block w-3 h-3 border-2 border-orange/30 border-t-orange rounded-full animate-spin align-middle" />}
            </p>
          )}
          {/* Grille teaser dense : le détail vit dans la FICHE (clic) —
              2 colonnes dès le mobile, comme un feed d'exploration.
              Dimée pendant une nouvelle recherche (résultats périmés). */}
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 transition-opacity ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
            {results.map((f, idx) => (
              <ProTeaserCard
                key={f.format_id || idx}
                format={f}
                idx={idx}
                airtableReady={airtableReady}
                isSaved={!!saveState[f.format_id]}
                onSave={() => saveFormat(f)}
                onUnsave={() => unsaveFormat(f)}
              />
            ))}
          </div>

          {results.length < total ? (
            <div ref={sentinelRef} className="text-center pt-4">
              <button onClick={loadMore} disabled={loading}
                className="px-6 py-3 rounded-xl bg-white border border-border text-sm font-bold text-ink hover:border-orange/30 transition-colors disabled:opacity-50">
                {loading ? 'Chargement…' : 'Voir plus de formats'}
              </button>
            </div>
          ) : results.length > 0 && (
            <p className="text-center pt-4 text-[12px] text-ink-muted">
              Tu as tout vu pour ces filtres.{allWorlds ? '' : ' Essaie « Voir tous les secteurs » ou'} Retire un filtre pour en découvrir d'autres.
            </p>
          )}
        </>
      )}
    </div>
  )
}

function FilterRow({ label, options, selected, onToggle, single }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = selected.includes(opt.key)
          return (
            <button key={opt.key} onClick={() => onToggle(opt.key)}
              className="text-xs px-2.5 py-1.5 rounded-full font-medium transition-all"
              style={{
                background: active ? 'var(--merlin-accent, #fe8204)' : 'white',
                color: active ? 'white' : 'rgb(74,68,88)',
                border: `1px solid ${active ? 'var(--merlin-accent, #fe8204)' : 'rgb(232,226,214)'}`,
              }}>
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIEW : VAULT — coffre personnel
// ═══════════════════════════════════════════════════════════════
function ProVault({ airtableReady, copy, copyState, onUnsave, preview, mockSaves, onMockUnsave }) {
  const [saves, setSaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all') // all | saved | draft | published

  async function fetchSaves() {
    // En preview, on lit les mocks au lieu d'appeler le backend
    if (preview) {
      const filtered = statusFilter === 'all'
        ? mockSaves
        : mockSaves.filter(s => s.status === statusFilter)
      setSaves(filtered)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const h = token ? { 'Authorization': `Bearer ${token}` } : {}
      const url = statusFilter === 'all'
        ? `${API_URL}/api/studio/saves`
        : `${API_URL}/api/studio/saves?status=${statusFilter}`
      const res = await fetch(url, { headers: h })
      const data = await res.json()
      setSaves(data.saves || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchSaves() }, [statusFilter, mockSaves])

  // Refetch quand un format est ajouté ailleurs (ex: deep-link "Adapter à mon
  // compte" qui sauvegarde puis bascule sur le coffre).
  useEffect(() => {
    const h = () => fetchSaves()
    window.addEventListener('studio:saves-changed', h)
    return () => window.removeEventListener('studio:saves-changed', h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  async function updateStatus(saveId, status) {
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const h = token ? { 'Authorization': `Bearer ${token}` } : {}
      await fetch(`${API_URL}/api/studio/saves/${saveId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ status }),
      })
      fetchSaves()
    } catch (e) { console.error('[updateStatus]', e.message) }
  }

  async function deleteSave(saveId) {
    if (!confirm('Retirer ce format de mes formats ?')) return
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const h = token ? { 'Authorization': `Bearer ${token}` } : {}
      await fetch(`${API_URL}/api/studio/saves/${saveId}`, { method: 'DELETE', headers: h })
      onUnsave?.(saveId)
      fetchSaves()
    } catch (e) { console.error('[delete]', e.message) }
  }

  const STATUS_TABS = [
    { key: 'all', label: 'Tous', count: saves.length },
    { key: 'saved', label: '★ Sauvegardés' },
    { key: 'draft', label: 'En cours' },
    { key: 'published', label: 'Publiés' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2">Mes formats</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-ink leading-tight">
          Tes formats <span className="text-orange">à toi</span>
        </h1>
      </div>

      {/* Tabs status */}
      <div className="flex gap-1 border-b border-border">
        {STATUS_TABS.map(t => (
          <button key={t.key}
            onClick={() => setStatusFilter(t.key)}
            className="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
            style={{
              borderColor: statusFilter === t.key ? 'var(--merlin-accent, #fe8204)' : 'transparent',
              color: statusFilter === t.key ? 'var(--merlin-accent, #fe8204)' : 'rgb(107,104,98)',
            }}>
            {t.label}
            {t.count != null && <span className="ml-1.5 text-xs opacity-70">({t.count})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
        </div>
      ) : saves.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white border border-border">
          <img src={merlinImg} alt="" className="w-14 h-14 mx-auto mb-3 opacity-50" />
          <p className="text-sm text-ink-muted mb-1">Tu n'as pas encore de formats sauvegardés.</p>
          <p className="text-xs text-ink-faint">Va sauvegarder des formats depuis le drop ou la bibliothèque.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {saves.map(save => (
            <VaultCard
              key={save.id}
              save={save}
              airtableReady={airtableReady}
              copy={copy}
              copyState={copyState}
              onStatusChange={(status) => updateStatus(save.id, status)}
              onDelete={() => deleteSave(save.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// FORMAT CARD (réutilisée par home + library)
// Mode "full" (script_adapted présent) = card complète avec toggle Pour toi/
//   Variables/Original + structure + why_for_you (cas drop hebdo)
// Mode "preview" (hook seul) = hook templatisé + bouton "Adapter à mon ton"
//   qui POST /api/studio/library/adapt et passe en mode full
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// PRO VIDEO PLAYER — custom (pas de controls natifs iOS qui montrent
// AirPlay/PiP/fullscreen et créent des bandes blanches sur le côté)
// Tap pour play/pause inline, doubletap pour ouvrir Instagram
// ═══════════════════════════════════════════════════════════════
// Registry global Pro pour auto-pause des autres vidéos quand une démarre
const _proVideos = new Set()
function _pauseOthersPro(current) {
  _proVideos.forEach(v => { if (v !== current && !v.paused) { try { v.pause() } catch {} } })
}

function ProVideoPlayer({ mp4Url, posterUrl, airtableReady, srcLien }) {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  // Son ON par défaut : l'user a cliqué Play explicitement → le navigateur
  // autorise l'audio (pas d'autoplay sans son requis). Cohérent avec
  // FormatCardFree.useState(false) côté StudioPage free.
  const [muted, setMuted] = useState(false)

  useEffect(() => {
    const v = videoRef.current; if (!v) return
    _proVideos.add(v)
    return () => { _proVideos.delete(v) }
  }, [])

  function handleMeta(e) {
    try { e.currentTarget.currentTime = 0.5 } catch {}
  }
  function togglePlay(e) {
    if (e) { e.preventDefault(); e.stopPropagation() }
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      _pauseOthersPro(v)
      v.play().then(() => setPlaying(true)).catch(() => {})
    } else {
      v.pause()
      setPlaying(false)
    }
  }
  function toggleMute(e) {
    e.preventDefault(); e.stopPropagation()
    const v = videoRef.current; if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
  }

  if (!mp4Url) {
    if (!airtableReady) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
        </div>
      )
    }
    if (srcLien) {
      return (
        <a href={srcLien} target="_blank" rel="noopener noreferrer"
          className="relative w-full h-full flex flex-col items-center justify-center gap-2 group">
          {posterUrl && <img src={posterUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" loading="lazy" />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,10,26,0.55), rgba(26,16,64,0.7))' }} />
          <div className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 4px 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' }}>
            <span className="text-white text-xl ml-0.5">▶</span>
          </div>
          <div className="relative z-10 text-center px-3">
            <span className="block text-sm font-bold text-white/95">Voir sur Instagram</span>
          </div>
        </a>
      )
    }
    return null
  }

  return (
    <>
      <video ref={videoRef}
        muted={muted} playsInline preload="metadata" disablePictureInPicture
        onLoadedMetadata={handleMeta}
        onClick={togglePlay}
        onPause={() => setPlaying(false)}
        onPlay={() => { _pauseOthersPro(videoRef.current); setPlaying(true) }}
        className="absolute inset-0 block cursor-pointer"
        style={{ width: '100%', height: '100%', objectFit: 'cover', background: 'var(--fmt-ph-1, #1e1145)' }}
        poster={posterUrl || undefined}>
        <source src={mp4Url} type="video/mp4" />
      </video>
      {/* Play button overlay quand pause */}
      {!playing && (
        <button onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center z-10 transition-opacity group"
          style={{ background: 'rgba(15,10,26,0.18)' }}>
          <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 16px rgba(0,0,0,0.30)' }}>
            <span className="text-purple-900 text-xl ml-0.5">▶</span>
          </span>
        </button>
      )}
      {/* Pause button (bottom-left, visible quand playing) */}
      {playing && (
        <button onClick={togglePlay}
          className="absolute bottom-2 left-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
          aria-label="Pause">
          <span className="text-white text-sm">⏸</span>
        </button>
      )}
      {/* Mute/unmute (bottom-right toujours visible) */}
      <button onClick={toggleMute}
        className="absolute bottom-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
        title={muted ? 'Activer le son' : 'Couper le son'}>
        <span className="text-white text-sm">{muted ? '🔇' : '🔊'}</span>
      </button>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// PRO TEASER CARD — miniature compacte pour la grille Découvrir.
// Le détail n'est plus inline : clic → la FICHE complète (doctrine
// viralité) via studio:open-format-detail. La grille redevient une
// grille (dense, scannable), la fiche devient LA vue d'analyse.
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// PRO DROP CARD — carte-couverture des formats "Pour toi".
// Uniquement la partie haute : visuel (SANS bouton play) + titre + les
// étiquettes (mécanique, arc, engagement). Toute la carte est cliquable,
// surbrillance + relief au survol, et le clic dévoile la FICHE complète
// (préchargée : l'analyse est déjà en cache quand on ouvre).
// ═══════════════════════════════════════════════════════════════
function ProDropCard({ format, idx, isSaved, onSave, onUnsave, airtableReady }) {
  const src = format.source || {}
  const beatImg = (format.beats_raw || format.structure || []).find(b => b && b.image_cle_url)?.image_cle_url || null
  const posterUrl = src.poster_url || beatImg || (airtableReady ? getVideoThumb({ lien: src.lien, airtable_id: src.airtable_id }) : null)
  const fmtNum = (n) => !n ? '0' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n)
  function open(e) {
    if (e) e.preventDefault()
    try { studioEvents.formatCardExpanded({ format_idx: idx, source_page: 'studio_pro_home', via: 'drop_cover' }) } catch { /* tracking only */ }
    try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format, idx, preload: true } })) } catch { /* no-op */ }
  }
  return (
    <article className="merlin-format-card overflow-hidden relative cursor-pointer transition-all duration-200 hover:-translate-y-1"
      style={format.is_daily_top
        ? { border: '2px solid var(--merlin-violet, #7c3aed)', boxShadow: '0 0 0 3px rgba(var(--merlin-violet-rgb, 124,58,237),0.18), 0 14px 38px rgba(var(--merlin-violet-rgb, 124,58,237),0.32)' }
        : undefined}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 0 2px rgba(var(--merlin-accent-rgb, 254,130,4),0.45), 0 18px 44px rgba(26,16,64,0.22)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = format.is_daily_top ? '0 0 0 3px rgba(var(--merlin-violet-rgb, 124,58,237),0.18), 0 14px 38px rgba(var(--merlin-violet-rgb, 124,58,237),0.32)' : '' }}
      onClick={open}>
      {format.is_daily_top && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-white"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', fontSize: '11px', fontWeight: 800, letterSpacing: '0.02em' }}>
          🔥 {format.daily_badge || "Viral aujourd'hui dans ta niche"}
        </div>
      )}
      {/* Visuel : miniature simple, pas de play (la vidéo se joue dans la fiche) */}
      <div className="relative overflow-hidden w-full aspect-[3/4] sm:aspect-[9/16] sm:max-h-[420px] studio-thumbnail"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
        {posterUrl && <img src={posterUrl} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.2) 0%, rgba(15,10,26,0) 30%, rgba(15,10,26,0) 60%, rgba(15,10,26,0.7) 100%)' }} />
        {/* Save — étoile (stopPropagation pour ne pas ouvrir la fiche) */}
        <button onClick={(e) => { e.stopPropagation(); isSaved ? onUnsave() : onSave() }}
          className="absolute top-2.5 right-2.5 z-20 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: isSaved ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.95)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          title={isSaved ? 'Retirer de mes formats' : 'Sauvegarder'}>
          <span style={{ fontSize: '16px', color: isSaved ? 'white' : 'rgb(107,104,98)' }}>{isSaved ? '★' : '☆'}</span>
        </button>
        {/* Affordance : voir le format */}
        <span className="absolute inset-x-0 bottom-2.5 z-10 text-center pointer-events-none">
          <span className="inline-block px-3 py-1.5 rounded-full text-[11px] font-bold text-white"
            style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.92)', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.45)' }}>🔍 Voir le format</span>
        </span>
      </div>
      {/* Header : n° + vues + titre + étiquettes, et RIEN d'autre */}
      <div className="merlin-card-header-orange px-3 sm:px-4 pt-2 sm:pt-3 pb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: 'var(--merlin-accent, #fe8204)' }}>
            Format {String(idx + 1).padStart(2, '0')}
          </span>
          {src.vues > 0 && <span className="text-[11px] text-ink-faint font-mono">{fmtNum(src.vues)} vues</span>}
        </div>
        <h3 className="text-sm font-bold text-ink leading-snug">{src.format_label || src.format_nom || 'Format viral'}</h3>
        <div className="flex items-center gap-1 flex-wrap mt-1.5">
          {src.mecanisme_viral && <span className="merlin-badge-mecanique" style={{ fontSize: '9px', padding: '2px 6px' }}>{src.mecanisme_viral}</span>}
          {src.arc_narratif && <span className="merlin-badge-chassis" style={{ fontSize: '9px', padding: '2px 6px' }}>▣ {src.arc_narratif}</span>}
          {src.engagement > 0 && <span className="merlin-badge-easy" style={{ fontSize: '9px', padding: '2px 6px' }}>◆ {src.engagement}% eng</span>}
        </div>
      </div>
    </article>
  )
}

export function ProTeaserCard({ format, idx, airtableReady, isSaved, onSave, onUnsave }) {
  const src = format.source || {}
  const posterUrl = src.poster_url || (airtableReady ? getVideoThumb({ lien: src.lien, airtable_id: src.airtable_id }) : null)
  const fmtNum = (n) => !n ? '0' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n)
  function open(e) {
    if (e) e.preventDefault()
    try { studioEvents.formatCardExpanded({ format_idx: idx, source_page: 'studio_pro_discover', via: 'teaser' }) } catch { /* tracking only */ }
    // Les 3 premiers formats sont préchauffés (warm-up ProLibrary) → la fiche
    // s'ouvre complète directement, sans passer par le bouton d'analyse.
    try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format, idx, preload: idx < 3 } })) } catch { /* no-op */ }
  }
  return (
    <article className="merlin-format-card overflow-hidden relative">
      <button type="button" onClick={open}
        className="relative block w-full text-left overflow-hidden aspect-[4/5] group active:scale-[0.99] transition-transform studio-thumbnail"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
        {posterUrl && (
          <img src={posterUrl} alt="" loading="lazy"
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.25) 0%, rgba(15,10,26,0) 32%, rgba(15,10,26,0) 55%, rgba(15,10,26,0.85) 100%)' }} />
        {/* Difficulté + fraîcheur — haut gauche */}
        <span className="absolute top-2 left-2 z-10 flex items-center gap-1">
          {src.difficulte && (
            <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white"
              style={{ background: src.difficulte === 'ultra' ? 'rgba(16,185,129,0.92)' : src.difficulte === 'facile' ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.90)' : 'rgba(234,88,12,0.85)', backdropFilter: 'blur(8px)' }}>
              {src.difficulte === 'ultra' ? '⚡ Ultra facile' : src.difficulte === 'facile' ? '✓ Facile' : '◆ Difficile'}
            </span>
          )}
          {src.is_recent && (
            <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white"
              style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.92)', backdropFilter: 'blur(8px)' }}>🕐 Récent</span>
          )}
        </span>
        {/* Vues — haut droite (sous l'étoile save) */}
        {src.vues > 0 && (
          <span className="absolute top-2 right-11 z-10 px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white font-mono"
            style={{ background: 'rgba(15,10,26,0.78)', backdropFilter: 'blur(8px)' }}>{fmtNum(src.vues)}</span>
        )}
        {/* Affordance analyse */}
        <span className="absolute inset-0 flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <span className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white"
            style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.95)', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.5)' }}>🔍 Voir l&rsquo;analyse</span>
        </span>
        {/* Bas : niche + titre */}
        <span className="absolute inset-x-0 bottom-0 p-2.5 z-10 block">
          <span className="block text-[8.5px] uppercase tracking-wider font-bold text-white/75 mb-0.5 truncate">{src.niche || ''}</span>
          <span className="block text-[12px] font-bold text-white leading-snug line-clamp-2">{src.format_label || src.format_nom || 'Format viral'}</span>
        </span>
      </button>
      {/* Save — étoile overlay (hors du bouton pour éviter le clic croisé) */}
      <button onClick={isSaved ? onUnsave : onSave}
        className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ background: isSaved ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.95)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
        title={isSaved ? 'Retirer de mes formats' : 'Sauvegarder'}>
        <span style={{ fontSize: '14px', color: isSaved ? 'white' : 'rgb(107,104,98)' }}>{isSaved ? '★' : '☆'}</span>
      </button>
    </article>
  )
}

function ProFormatCard({ format: initialFormat, idx, airtableReady, isSaved, onSave, onUnsave, copy, copyState, openSignal = 0 }) {
  const [format, setFormat] = useState(initialFormat)
  // Ref sur le <details> du script : permet à l'onboarding / la checklist de
  // déplier la 1ère carte programmatiquement (CTA "Ouvre mon premier format").
  const scriptDetailsRef = useRef(null)
  // Déploie + scrolle quand openSignal change (ignore la valeur initiale 0).
  useEffect(() => {
    if (!openSignal) return
    const el = scriptDetailsRef.current
    if (!el) return
    el.open = true
    // onToggle ne fire pas toujours sur un set programmatique selon le moteur
    // → on coche l'étape activation explicitement ici aussi (idempotent).
    markActivationStep('format')
    try { window.dispatchEvent(new CustomEvent('studio:format-opened', { detail: { format_idx: idx } })) } catch {}
    try { studioEvents.formatCardExpanded({ format_idx: idx, source_page: 'studio_pro', via: 'onboarding' }) } catch {}
    setTimeout(() => { try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch {} }, 120)
  }, [openSignal]) // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle manuel du <details> par l'user (clic sur le summary) → activation +
  // tracking. Coché une seule fois côté activation (markActivationStep idempotent).
  function handleScriptToggle(e) {
    if (!e?.target?.open) return
    markActivationStep('format')
    try { window.dispatchEvent(new CustomEvent('studio:format-opened', { detail: { format_idx: idx } })) } catch {}
    try { studioEvents.formatCardExpanded({ format_idx: idx, source_page: 'studio_pro', via: 'user' }) } catch {}
  }
  // Default sur "trame" : la structure validée est plus utile que le
  // script AI generic (feedback users : éviter le rendu cheap, garder
  // le contrôle créatif).
  const [tab, setTab] = useState('trame')
  const [adapting, setAdapting] = useState(false)
  // State d'animation au click save (déclenche pop + ring 700ms)
  const [savePulse, setSavePulse] = useState(false)
  const src = format.source || {}
  const mp4Url = airtableReady ? getVideoMp4({ lien: src.lien, airtable_id: src.airtable_id }) : null
  // Poster : beat-frame Storage (fourni par la recherche) puis miniature
  // Airtable en repli — sans ça, le Découvrir affichait un mur de cadres vides.
  const posterUrl = src.poster_url || (airtableReady ? getVideoThumb({ lien: src.lien, airtable_id: src.airtable_id }) : null)

  // Ouvre la fiche complète (doctrine viralité) dans FormatDetailModal.
  function openFiche(e) {
    if (e) { e.preventDefault(); e.stopPropagation() }
    try { studioEvents.formatCardExpanded({ format_idx: idx, source_page: 'studio_pro', via: 'fiche' }) } catch { /* tracking only */ }
    try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format, idx } })) } catch { /* no-op */ }
  }

  function handleSaveClick() {
    if (isSaved) { onUnsave(); return }
    setSavePulse(true)
    setTimeout(() => setSavePulse(false), 700)
    onSave()
  }

  // Mode "full" si le format a déjà été adapté (drop hebdo OU after click adapt)
  const isFullMode = !!(format.script_adapted && format.hook_adapted)

  const fmtNum = (n) => {
    if (!n) return '0'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return String(n)
  }

  function renderText(text) {
    if (!text) return <em className="text-ink-faint">Indisponible</em>
    const parts = text.split(/(\[[^\]]+\])/g)
    return parts.map((p, i) =>
      p.startsWith('[') && p.endsWith(']')
        ? <span key={i} className="inline-block px-1.5 py-0.5 mx-0.5 rounded-md text-[0.95em] font-bold"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.14)', color: '#d97206' }}>{p}</span>
        : <span key={i}>{p}</span>
    )
  }

  async function adaptToMe() {
    if (adapting || !format.format_id) return
    setAdapting(true)
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const h = token ? { 'Authorization': `Bearer ${token}` } : {}
      const res = await fetch(`${API_URL}/api/studio/library/adapt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ format_id: format.format_id }),
      })
      const data = await res.json()
      if (data?.script_adapted) {
        setFormat(prev => ({ ...prev, ...data }))
      }
    } catch (e) { console.error('[adapt]', e.message) }
    finally { setAdapting(false) }
  }

  return (
    <article className="merlin-format-card flex flex-col"
      style={format.is_daily_top ? { border: '2px solid var(--merlin-violet, #7c3aed)', boxShadow: '0 0 0 3px rgba(var(--merlin-violet-rgb, 124,58,237),0.18), 0 14px 38px rgba(var(--merlin-violet-rgb, 124,58,237),0.32)' } : undefined}>
      {/* Ruban FORMAT DU JOUR (slot #1 = viral frais du jour dans la niche) */}
      {format.is_daily_top && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-white"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', fontSize: '11px', fontWeight: 800, letterSpacing: '0.02em' }}>
          🔥 {format.daily_badge || "Viral aujourd'hui dans ta niche"}
        </div>
      )}
      {/* Vidéo — wrapper + custom player + bouton save overlay
          aspect-[3/4] sur mobile (compromis compact vs IG reel 9:16),
          sm:aspect-[9/16] sur desktop. width 100% du card pour ne JAMAIS
          laisser de bande blanche à côté. NE PAS combiner maxHeight et
          aspectRatio (ça écrase la largeur). */}
      <div className="relative overflow-hidden w-full aspect-[3/4] sm:aspect-[9/16] sm:max-h-[500px] studio-thumbnail"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
        <ProVideoPlayer mp4Url={mp4Url} posterUrl={posterUrl} airtableReady={airtableReady} srcLien={src.lien} />
        {/* Lien source : admin uniquement */}
        <AdminSourceLink lien={src.lien} className="!top-2.5 !left-2.5 !w-9 !h-9" />
        {/* Save heart icon — overlay */}
        <button onClick={isSaved ? onUnsave : onSave}
          className="absolute top-2.5 right-2.5 z-20 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{
            background: isSaved ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.95)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          title={isSaved ? 'Retirer de mes formats' : 'Sauvegarder'}>
          <span style={{ fontSize: '16px', color: isSaved ? 'white' : 'rgb(107,104,98)' }}>
            {isSaved ? '★' : '☆'}
          </span>
        </button>
      </div>

      {/* Header — visible toujours */}
      <div className="merlin-card-header-orange px-3 sm:px-4 pt-2 sm:pt-3 pb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: 'var(--merlin-accent, #fe8204)' }}>
            Format {String(idx + 1).padStart(2, '0')}
          </span>
          <span className="flex items-center gap-1.5">
            {/* Badge difficulté de reproduction (recalibrage duplicabilité) */}
            {src.difficulte && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={src.difficulte === 'ultra'
                  ? { background: 'rgba(16,185,129,0.13)', color: '#0e9f6e' }
                  : src.difficulte === 'facile'
                    ? { background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: '#d97206' }
                    : { background: 'rgba(234,88,12,0.10)', color: '#c2410c' }}>
                {src.difficulte === 'ultra' ? '⚡ Ultra facile' : src.difficulte === 'facile' ? '✓ Facile' : '◆ Difficile'}
              </span>
            )}
            {src.vues > 0 && <span className="text-[11px] text-ink-faint font-mono">{fmtNum(src.vues)} vues</span>}
          </span>
        </div>
        <h3 className="text-sm font-bold text-ink leading-snug">{src.format_label || src.format_nom || 'Format viral'}</h3>
        {/* Fiche complète (doctrine viralité) : pourquoi elle explose + comment la refaire */}
        <button type="button" onClick={openFiche}
          className="mt-2 w-full py-2 rounded-lg text-[12px] font-bold transition-colors"
          style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.09)', color: 'var(--merlin-violet, #7c3aed)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}>
          🔍 Pourquoi elle explose · l&rsquo;analyse complète
        </button>
        {src.createur && <p className="text-[11px] text-ink-faint mt-0.5">par <span className="font-bold">{src.createur}</span></p>}
      </div>

      {/* Badges — visibles toujours */}
      <div className="px-3 sm:px-4 pt-1 pb-2 flex items-center gap-1.5 flex-wrap">
        {src.mecanisme_viral && <span className="merlin-badge-mecanique">{src.mecanisme_viral}</span>}
        {src.arc_narratif && <span className="merlin-badge-chassis">▣ {src.arc_narratif}</span>}
        {src.engagement > 0 && <span className="merlin-badge-easy">◆ {src.engagement}% eng</span>}
      </div>

      {/* TOUT le reste collapsé par défaut — bouton voyant qui incite à dérouler */}
      <details ref={scriptDetailsRef} onToggle={handleScriptToggle} className="group border-t-2 flex-1"
        style={{ borderColor: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
        <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-3 sm:px-4 py-3.5 transition-colors"
          style={{ background: 'linear-gradient(90deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.10), rgba(245,158,11,0.05))' }}>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] uppercase tracking-wider font-bold text-orange mb-0.5">
              {isFullMode ? '✨ Personnalisé pour toi' : '📜 Aperçu du script'}
            </p>
            <p className="text-sm font-bold text-ink leading-snug">
              {isFullMode ? 'Le script adapté à ton contenu' : 'Découvre hook + script complet'}
            </p>
          </div>
          <Chevron accent="orange" />
        </summary>
        <div className="pt-1 pb-3">

          {/* Why for you */}
          {isFullMode && format.why_for_you && (
            <div className="px-3 sm:px-4 pt-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-1">✨ Pour toi</p>
              <p className="text-xs text-ink leading-relaxed italic">{format.why_for_you}</p>
            </div>
          )}

          {/* Tabs : Ta trame (default) / Variables / Original.
              Suite feedback users : le script IA "Pour toi" est jugé cheap,
              remplacé par la trame structurée (beats + objectif + à mettre).
              Le script_adapted reste backend pour usage futur ou export. */}
          {isFullMode && (
            <div className="px-3 sm:px-4 pt-3">
              <div className="flex gap-0.5 bg-ink/5 rounded-lg p-0.5">
                {[
                  { v: 'trame', l: 'Ta trame' },
                  { v: 'template', l: 'Variables' },
                  { v: 'source', l: 'Original', show: !!(src.script_original && src.script_original.trim().length > 30) },
                ].filter(opt => opt.show !== false).map(opt => (
                  <button key={opt.v} onClick={(e) => { e.preventDefault(); setTab(opt.v); studioEvents.tabClicked({ tab: opt.v, format_idx: idx, source_page: 'studio_pro' }) }}
                    className={`flex-1 text-[11px] px-2 py-2 rounded-md font-bold transition-colors ${tab === opt.v ? 'bg-white shadow-sm text-ink' : 'text-ink-muted hover:text-ink'}`}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hook visible toujours (sauf en mode trame qui inclut déjà le beat 1) */}
          {tab !== 'trame' && (
            <div className="px-3 sm:px-4 pt-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-1">Hook</p>
              <p className="text-sm font-bold text-ink leading-snug italic">
                « {!isFullMode ? (format.hook_template || src.hook_original || 'Hook indisponible')
                  : tab === 'template' ? renderText(format.hook_template)
                  : (src.hook_original || 'Hook original indisponible')
                } »
              </p>
            </div>
          )}

          {/* Contenu selon tab (full mode) */}
          {isFullMode && (
            <div className="px-3 sm:px-4 pt-3">
              {tab === 'trame' && <TrameContent beats={format.beats_raw || format.structure || []} mode={format.texte_ecran_mode || format.source?.texte_ecran_mode} />}
              {tab === 'template' && (() => {
                let tmpl = cleanScriptTemplate(format.script_template || '')
                const hasVars = /\[[^\]]+\]/.test(tmpl)
                // Variables = script PARLÉ. Format visuel (recette sans [variables])
                // → on bascule sur le vrai script parlé, sinon renvoi à la trame.
                const fsrc = format.source || {}
                const spoken = (format.script_fr || fsrc.script_fr || fsrc.script_original || '').trim()
                if (!hasVars && spoken) tmpl = spoken
                if (!hasVars && !spoken) return <p className="text-[11px] text-ink-muted leading-snug">Format surtout visuel : le détail (plans, cadrage, à filmer) est dans l'onglet <strong className="text-ink">Ta trame</strong>.</p>
                if (!tmpl) return <em className="text-ink-faint text-[11px]">Template indisponible.</em>
                const wordCount = tmpl.split(/\s+/).filter(Boolean).length
                const isLong = tmpl.length > 280
                if (!isLong) {
                  return (
                    <>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">Script template</p>
                      <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/70 rounded-lg p-3 border border-border">
                        {renderText(tmpl)}
                      </div>
                    </>
                  )
                }
                return (
                  <>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">Script template</p>
                    <details className="group">
                      <summary className="cursor-pointer list-none bg-bg/70 rounded-lg p-3 border border-border">
                        <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed">
                          {renderText(tmpl.substring(0, 220))}<span className="text-ink-faint">…</span>
                        </div>
                        <p className="text-[10px] text-orange font-bold mt-2 group-open:hidden flex items-center gap-1">
                          <span>+</span> Voir le template complet ({wordCount} mots)
                        </p>
                        <p className="text-[10px] text-ink-faint font-bold mt-2 hidden group-open:flex items-center gap-1">
                          <span>−</span> Replier
                        </p>
                      </summary>
                      <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/70 rounded-lg p-3 border border-border mt-1">
                        {renderText(tmpl)}
                      </div>
                    </details>
                  </>
                )
              })()}
              {tab === 'source' && (() => {
                const txt = src.script_original
                if (!txt) return <em className="text-ink-faint text-[11px]">Transcription indisponible.</em>
                const wordCount = txt.split(/\s+/).filter(Boolean).length
                const isLong = txt.length > 280
                if (!isLong) {
                  return (
                    <>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">Transcription originale</p>
                      <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/70 rounded-lg p-3 border border-border">
                        {txt}
                      </div>
                    </>
                  )
                }
                return (
                  <>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">Transcription originale</p>
                    <details className="group">
                      <summary className="cursor-pointer list-none bg-bg/70 rounded-lg p-3 border border-border">
                        <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed">
                          {txt.substring(0, 220)}<span className="text-ink-faint">...</span>
                        </div>
                        <p className="text-[10px] text-orange font-bold mt-2 group-open:hidden flex items-center gap-1">
                          <span>+</span> Voir le script complet ({wordCount} mots)
                        </p>
                        <p className="text-[10px] text-ink-faint font-bold mt-2 hidden group-open:flex items-center gap-1">
                          <span>−</span> Replier
                        </p>
                      </summary>
                      <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/70 rounded-lg p-3 border border-border mt-1">
                        {txt}
                      </div>
                    </details>
                  </>
                )
              })()}
            </div>
          )}

          {/* Script source (preview mode, pas encore adapté) */}
          {!isFullMode && src.script_original && (
            <div className="px-3 sm:px-4 pt-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">Script source original</p>
              <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/60 rounded-lg p-3 border border-border max-h-[200px] overflow-y-auto">
                {src.script_original}
              </div>
            </div>
          )}
        </div>
      </details>

      {/* CTAs : action principale + save explicite */}
      <div className="px-4 pt-4 pb-4 mt-auto space-y-2">
        {isFullMode ? (
          <button
            onClick={() => {
              // Le bouton copie selon le tab actif. En mode "trame" on copie
              // une représentation texte de la trame (numéros + fonctions
              // + objectifs) pour que l'user puisse partir avec.
              let toCopy = ''
              if (tab === 'trame') {
                const beatsForCopy = format.beats_raw || format.structure || []
                toCopy = beatsForCopy.map((b, i) => `${b.beat || i + 1}. ${b.fonction || 'Étape'} | ${(b.texte_cle || '').substring(0, 200)}`).join('\n')
              } else if (tab === 'template') {
                const t = cleanScriptTemplate(format.script_template || '')
                const fsrc = format.source || {}
                toCopy = /\[[^\]]+\]/.test(t) ? t : (format.script_fr || fsrc.script_fr || fsrc.script_original || t)
              } else {
                toCopy = src.script_original || ''
              }
              copy(toCopy, `pro-${format.format_id}`)
            }}
            className="w-full py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01]"
            style={{
              background: copyState[`pro-${format.format_id}`]
                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
              boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.25)',
            }}>
            {copyState[`pro-${format.format_id}`]
              ? '✓ Copié, à toi de filmer'
              : tab === 'trame' ? 'Copier la trame' : 'Copier le script'}
          </button>
        ) : (
          <button
            onClick={adaptToMe}
            disabled={adapting}
            className="w-full py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01] disabled:opacity-60"
            style={{
              background: adapting ? 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
              boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.25)',
            }}>
            {adapting ? '✨ Merlin écrit ton script…' : '✨ Adapter le format'}
          </button>
        )}

        {/* Bouton sauvegarder explicite — animé au click */}
        <button
          onClick={handleSaveClick}
          className={`studio-save-btn relative w-full py-2 rounded-xl text-xs font-bold transition-all border active:scale-95 ${savePulse ? 'studio-save-pulse' : ''}`}
          style={{
            background: isSaved ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)' : 'white',
            borderColor: isSaved ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.45)' : 'rgb(232,226,214)',
            color: isSaved ? 'var(--merlin-accent, #fe8204)' : 'rgb(74,68,88)',
          }}>
          {/* Ring qui s'élargit au click */}
          {savePulse && (
            <span className="absolute inset-0 rounded-xl pointer-events-none studio-save-ring"
              style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }} />
          )}
          <span className="relative inline-flex items-center justify-center gap-1.5">
            <span className={`inline-block transition-transform ${savePulse ? 'studio-star-pop' : ''}`}
              style={{ fontSize: '14px' }}>
              {isSaved ? '★' : '☆'}
            </span>
            <span>{isSaved ? 'Dans Mes formats — retirer' : 'Ajouter à Mes formats'}</span>
          </span>
        </button>
      </div>
    </article>
  )
}

// ═══════════════════════════════════════════════════════════════
// VAULT CARD (avec statuts éditables)
// ═══════════════════════════════════════════════════════════════
function VaultCard({ save, airtableReady, copy, copyState, onStatusChange, onDelete }) {
  const f = save.format_data || {}
  const src = f.source || {}
  const mp4Url = airtableReady ? getVideoMp4({ lien: src.lien, airtable_id: src.airtable_id }) : null
  // Tab actif : Ta trame (default) / Variables / Original.
  // Aligné sur la carte format complète : la trame structurée (beats +
  // objectif + à mettre) est l'essentiel, pas le script "Pour toi" jugé cheap.
  // Fallback "Pour toi" gardé UNIQUEMENT pour les vieux saves sans beats.
  const beats = f.beats_raw || f.structure || []
  const hasTrame = Array.isArray(beats) && beats.length > 0
  const [tab, setTab] = useState(hasTrame ? 'trame' : 'adapted')

  const fmtNum = (n) => {
    if (!n) return '0'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return String(n)
  }

  const STATUS_COLORS = {
    saved: { bg: 'bg-gray-100', txt: 'text-gray-700', label: '★ Sauvegardé' },
    draft: { bg: 'bg-orange/15', txt: 'text-orange', label: '✎ En cours' },
    published: { bg: 'bg-green-100', txt: 'text-green-700', label: '✓ Publié' },
    archived: { bg: 'bg-gray-50', txt: 'text-gray-500', label: '◌ Archivé' },
  }
  const status = STATUS_COLORS[save.status] || STATUS_COLORS.saved

  const hookByTab = {
    adapted: f.hook_adapted || f.hook_template || src.hook_original || '',
    template: f.hook_template || '',
    source: src.hook_original || '',
  }
  const scriptByTab = {
    adapted: f.script_adapted || '',
    template: f.script_template || '',
    source: src.script_original || '',
  }
  const hasOriginalScript = !!(src.script_original && src.script_original.trim().length > 30)
  const tabs = [
    { v: 'trame', l: 'Ta trame', show: hasTrame },
    { v: 'adapted', l: 'Pour toi', show: !hasTrame && !!(f.script_adapted) },
    { v: 'template', l: 'Variables', show: !!(f.script_template) },
    { v: 'source', l: 'Original', show: hasOriginalScript },
  ].filter(t => t.show)

  return (
    <article className="merlin-format-card flex flex-col">
      <div className="relative overflow-hidden bg-black/95"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)', aspectRatio: '16/10' }}>
        {mp4Url ? (
          <video controls preload="metadata" playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src={mp4Url} type="video/mp4" />
          </video>
        ) : src.lien ? (
          <a href={src.lien} target="_blank" rel="noopener noreferrer"
            className="relative w-full h-full flex items-center justify-center group">
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,10,26,0.55), rgba(26,16,64,0.7))' }} />
            <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.92)' }}>
              <span className="text-purple-900 text-sm ml-0.5">▶</span>
            </div>
          </a>
        ) : null}

        <div className="absolute top-2 left-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${status.bg} ${status.txt}`}>
            {status.label}
          </span>
        </div>
        {/* Lien source : admin uniquement */}
        <AdminSourceLink lien={src.lien} className="!left-auto !right-2 !top-2" />
      </div>

      <div className="px-4 pt-3 pb-2">
        <h3 className="text-sm font-bold text-ink leading-snug">{src.format_label || src.format_nom || 'Format viral'}</h3>
        {src.createur && (
          <p className="text-[11px] text-ink-faint mt-0.5">
            par <span className="font-bold">{src.createur}</span>
            <span className="mx-1.5">·</span>
            <span className="font-mono">{fmtNum(src.vues)} vues</span>
          </p>
        )}
      </div>

      {/* Tabs 3 modes (Pour toi / Variables / Original) */}
      {tabs.length > 1 && (
        <div className="px-4 pt-2">
          <div className="flex gap-0.5 bg-ink/5 rounded-lg p-0.5">
            {tabs.map(opt => (
              <button key={opt.v} onClick={() => setTab(opt.v)}
                className={`flex-1 text-[10px] px-2 py-1.5 rounded-md font-bold transition-colors ${tab === opt.v ? 'bg-white shadow-sm text-ink' : 'text-ink-muted hover:text-ink'}`}>
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hook (dynamique par tab) — caché en mode trame (le beat 1 l'inclut déjà) */}
      {tab !== 'trame' && (
        <div className="px-4 pt-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-1">
            {f.is_visual_primary ? 'Hook visuel' : 'Hook'}
          </p>
          <p className="text-sm font-bold text-ink leading-snug italic line-clamp-3">
            « {hookByTab[tab] || '(vide)'} »
          </p>
        </div>
      )}

      {/* Contenu (dynamique par tab) — trame structurée OU script, scrollable */}
      <div className="px-4 pt-3 flex-1 min-h-0">
        {tab === 'trame' ? (
          <div className="max-h-[280px] overflow-y-auto">
            <TrameContent beats={beats} mode={f.texte_ecran_mode || src.texte_ecran_mode} />
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">
              {f.is_visual_primary
                ? (tab === 'source' ? 'Description visuelle' : 'Recette visuelle')
                : (tab === 'source' ? 'Transcription originale' : 'Script complet')}
            </p>
            <div className="text-[12px] text-ink whitespace-pre-line leading-relaxed bg-bg/70 rounded-lg p-3 border border-border max-h-[220px] overflow-y-auto">
              {scriptByTab[tab] || <em className="text-ink-faint">(indisponible)</em>}
            </div>
          </>
        )}
      </div>

      {/* Status actions + Copy active tab + Delete */}
      <div className="px-4 pt-3 pb-4 mt-auto space-y-2">
        <div className="flex gap-1">
          {['saved', 'draft', 'published'].map(s => (
            <button key={s} onClick={() => onStatusChange(s)}
              className="flex-1 text-[10px] px-2 py-1.5 rounded-md font-bold transition-colors"
              style={{
                background: save.status === s ? 'var(--merlin-accent, #fe8204)' : 'rgba(var(--merlin-accent-rgb, 254,130,4),0.08)',
                color: save.status === s ? 'white' : 'var(--merlin-accent, #fe8204)',
              }}>
              {s === 'saved' ? '★' : s === 'draft' ? '✎' : '✓'} {STATUS_COLORS[s].label.split(' ')[1]}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          <button onClick={() => {
              const toCopy = tab === 'trame'
                ? beats.map((b, i) => `${b.beat || i + 1}. ${b.fonction || 'Étape'} | ${(b.texte_cle || '').substring(0, 200)}`).join('\n')
                : scriptByTab[tab]
              copy(toCopy, `vault-${save.id}-${tab}`)
            }}
            className="flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all"
            style={{
              background: copyState[`vault-${save.id}-${tab}`]
                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
            }}>
            {copyState[`vault-${save.id}-${tab}`] ? '✓ Copié' : `Copier ${tabs.find(t => t.v === tab)?.l || ''}`}
          </button>
          <button onClick={onDelete}
            className="px-3 py-2 rounded-lg text-xs text-red-600 hover:bg-red-50 transition-colors">
            🗑
          </button>
        </div>
      </div>
    </article>
  )
}
